import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Call, Device } from '@twilio/voice-sdk';
import type { CockpitClientFrame, LeadCall, VoiceToken } from './protocol';
import { voicemailConfig, isVoicemail, remoteAudible, localAudible } from './voicemail';
import {
  newSilenceGate,
  observeSilence,
  shouldFire,
  markFired,
  markDismissed,
  isRaised,
  cardAge,
  reactionMs,
  gateMode,
  type SilenceGateState,
  type SilenceRule,
} from './silence-gate';
import { reportAudioSnapshot, reportAudioFailure, type AudioReportReason } from './audio-report';
import { buildAudioSnapshot } from './audio-devices';
import {
  micState,
  micBannerCopy,
  micEpisodeStart,
  micRestoreStep,
  newMicHealth,
  WARN_MS,
  BLOCK_MS,
  RETRY_AFTER_MS,
} from './mic-health';
import type { MicBannerCopy, MicHealth, MicPermission, MicState } from './mic-health';
import type { InboundDecision } from './inbound-guard';
import { buildPreflightLog, preflightSummary, type PreflightReportLike } from './preflight-report';
import { newSetupTiming, attachSetupProbe, setupReport } from './call-setup-timing';
import type { SetupTiming } from './call-setup-timing';
import { newMicLevel, observeLevel, reportLevel } from './mic-level';
import { newMediaStats, attachMediaStatsPoller } from './call-media-stats';
import { newMicTrack, trackAccepted, trackSeen, trackEnd, reportTrack } from './mic-track';
import {
  INPUT_LEVEL_WARNING,
  newInputWarning,
  inputWarningRaised,
  inputWarningCleared,
  reportInputWarning,
} from './sdk-warnings';
import {
  newSilentMic,
  observeMic,
  raiseFromSdk,
  clearFromSdk,
  type MicSilenceSource,
  type MicTransition,
} from './silent-mic';
import {
  edgeParamOf,
  encodeArm,
  decodeArm,
  defaultEdgeFor,
  deviceEdgeOptions,
  preflightEdgeOptions,
  resolveEdge,
  LS_EDGE,
} from './twilio-edge';
import type { Arm, EdgeSource, ResolvedEdge, SelectableEdge } from './twilio-edge';
import {
  decodeApplied,
  detectChrome,
  encodeApplied,
  MAX_SAMPLES,
  medianOf,
  newVerdictState,
  pqUi,
  rawVerdict,
  settleVerdict,
  LS_PQ_APPLIED,
} from './pq-fix';
import type { PqUi, PqVerdict, VerdictState } from './pq-fix';
import {
  newAudioLiveness,
  markMuted,
  markUnmuted,
  observe as observeLiveness,
  report as reportLiveness,
} from './audio-liveness';
import {
  decideRebuild,
  matchesIceRestartCrash,
  mergePending,
  record31005,
  terminalErrorReason,
  watchdogStep,
  TOKEN_REASK_BASE_MS,
  TOKEN_REASK_MAX_MS,
  WAKE_REFRESH_MIN_GAP_MS,
  WATCHDOG_TICK_MS,
} from './device-rebuild';
import type { PendingRebuild, RebuildReason, WatchdogState } from './device-rebuild';
import { rumAction, logCall } from '../lib/rum';

// =====================================================================
// useTwilioPhone — the SDR's browser softphone, built on the Twilio Voice
// JavaScript SDK (@twilio/voice-sdk). Gated behind login.
//
// Docs: https://www.twilio.com/docs/voice/sdks/javascript (+ /best-practices)
//
// The Voice access token is minted by the CORE (keyed by the SDR's email) and
// PUSHED to the SPA by the BFF over the WebSocket (`voice_token` frame) — the
// browser never fetches it or talks to the core. This hook consumes it:
//   - first real token  -> new Device(token, { codecPreferences:[opus,pcmu] }) + register()
//   - on register        -> setInputDevice('default') (the ONE mic stream) + inputVolume metering
//   - token changes      -> device.updateToken(token)   (refresh, no rebuild)
//   - tokenWillExpire    -> onRefreshNeeded()            (asks the BFF over the WS)
//   - inbound call       -> wire it + accept()           (predictive dial)
//   - disable / unmount  -> device.destroy()
//
// ONE MICROPHONE: all input metering rides Twilio's own mic stream
// (`device.audio.setInputDevice('default')` + the `inputVolume` event) — we do
// NOT open a second getUserMedia. Remote/output level comes off the live call
// (`call.on('volume')`). Output volume is the only Web Audio we add: a GainNode
// on the call's playback element (output, never a mic).
//
// NO SIMULATION: until the core's /dialer/rtc/token is live the BFF sends
// token=null, so there is no Device and `status` stays `idle` — the cockpit
// shows the Twilio controls (volume / test) and waves as DISABLED rather
// than faking a connected phone. Everything lights up only against a real,
// registered Device; the moment a real token arrives the metering comes alive.
// =====================================================================

export type TwilioStatus = 'idle' | 'connecting' | 'ready' | 'on_call' | 'error';

export interface ConnectionTestState {
  status: 'idle' | 'running' | 'done' | 'failed';
  // What to show the SDR. Never a verdict on their connection — see preflight-report.ts.
  message: string | null;
}

// Coarse call-audio quality derived from the RTCSample stream (MOS/RTT/loss).
export type CallQuality = 'excellent' | 'good' | 'degraded' | 'unknown';

export interface TwilioPhone {
  status: TwilioStatus;
  // True from call-accept until the far side FIRST produces audio (above the
  // voicemail remoteMax) — i.e. "connected, but the person hasn't spoken yet".
  // Drives the cockpit's "Connecting to …" label; flips false the instant remote
  // audio is heard (decoupled from the voicemail timer), and resets on call end.
  awaitingRemote: boolean;
  // True once the silent-voicemail heuristic tripped (dead air while we spoke, no
  // confirmed human) — drives the "couldn't confirm a human" banner. Auto-clears when
  // the far side speaks, a real `human` verdict lands, or the call ends.
  humanUnconfirmed: boolean;
  // The silence gate's prompt (cockpit/silence-gate.ts), or null while down.
  // `deadlineAt` (epoch ms) is the countdown's end — always set while raised (ON
  // is the full behaviour; there is no promptless state). `rule` is which tier
  // fired — the banner words the two differently.
  silencePrompt: { deadlineAt: number | null; rule: SilenceRule } | null;
  // The prompt's Continue button: stand down for the rest of this call.
  staySilenceGate: () => void;
  lastError: string | null;
  startCall: () => void;
  endCall: () => void;
  // --- controls + telemetry (all Twilio-native; need a live registered Device) ---
  micSilence: { source: MicSilenceSource } | null;
  // Output (speaker) volume, 0.2–1.0 — floored at 20% so it never fully mutes.
  outputVolume: number;
  setOutputVolume: (v: number) => void;
  // rAF-friendly level accessors (0–1); read from refs, NOT React state, so the
  // ~60Hz volume stream never re-renders.
  getInputLevel: () => number;
  getOutputLevel: () => number;
  // True once the Device reports the browser can analyse input volume.
  volumeSupported: boolean;
  // Live-call telemetry (from `sample`/`warning`); reset when the call ends.
  quality: CallQuality;
  warnings: string[];
  deviceRtt: number | null;
  // Plays Twilio's built-in test tone through the speaker device.
  playTestTone: () => void;
  // Runs Twilio's preflight against their echo service and reports the phase-by-phase
  // connection breakdown. Manual, because it places a real (short, billable) test call.
  runConnectionTest: () => void;
  // 'idle' | 'running' | 'done' | 'failed', plus the line shown to the SDR.
  connectionTest: ConnectionTestState;
  // --- audio device selection (mic in / speaker out) ---
  inputDevices: MediaDeviceInfo[];
  outputDevices: MediaDeviceInfo[];
  currentInputId: string | null;
  currentOutputId: string | null;
  // False on browsers without setSinkId (Firefox/Safari) — hide the speaker picker.
  outputSelectionSupported: boolean;
  selectInputDevice: (deviceId: string) => void;
  selectOutputDevice: (deviceId: string) => void;
  // Set when the active device was lost and we couldn't restore a remembered one
  // (e.g. headset unplugged → browser fell back to a dead "default"). null = ok.
  deviceAlert: string | null;
  // True once softphone auto-recovery hit its hourly rebuild cap and stopped —
  // the SDR must refresh the page. Separate from deviceAlert on purpose: the
  // mic paths overwrite/clear that slot, and both facts can be true at once.
  rebuildCapped: boolean;
  // The label of a connected external mic that ISN'T the one in use, while a
  // built-in mic is live — the SDR has a headset attached and is dialing on the
  // laptop mic. Drives the mic-picker highlight. null = nothing to nag about.
  idleExternalMic: string | null;
  // 'warn' speaks; 'blocked' also gates the pool picker. Deliberately NOT derived
  // from `lastError`: a single acquire rejection is usually transient.
  micState: MicState;
  // What to show while warn/blocked; null otherwise.
  micBanner: MicBannerCopy | null;
  // Triage fields for the blocked log, not the UI. `lastError` above is a display
  // string covering speakers too, and `blocked` is reachable with no retry at all —
  // on a permission flip, or an acquire that never settles.
  micPermission: MicPermission;
  micError: string | null;
  micRetried: boolean;
  // Re-run the whole acquire — raising the browser's permission prompt if it can.
  // Safe to call repeatedly.
  recheckMic: () => void;
  // Lend the microphone out and take it back, for anything in this tab that needs
  // to be its sole holder. Automatic re-acquire stands down in between. `restoreMic`
  // resolves false only once the mic is genuinely gone.
  releaseMic: () => Promise<void>;
  restoreMic: () => Promise<boolean>;
  // --- connection region (the Twilio edge) ---
  // What this session will connect on — the picker's value. What is stored, or with
  // nothing stored, whatever this browser's region defaults to.
  requestedEdge: SelectableEdge | null;
  // `geo` means nobody chose it, which the picker has to say out loud: a region an
  // SDR never picked, shown without explanation, reads as a bug.
  edgeSource: EdgeSource | null;
  // What the region WOULD default to whatever is stored. `edgeSource` cannot answer
  // that — it goes null on opt-out, while the default is still what they opted out of.
  geoEdge: SelectableEdge | null;
  // Whether the stored value differs from what the live Device was built with. A
  // Device cannot change edge without being rebuilt, so a change waits for a reload.
  // Computed here rather than in the view because it needs the Device lifecycle:
  // before one exists there is nothing to differ from, and comparing against a
  // not-yet-built Device would show every armed SDR a permanent "reload" prompt for
  // a change they never made.
  edgePending: boolean;
  // What Twilio actually attached to, which can differ from what was requested if
  // the request was ignored or fell through to the chain's terminal `roaming`.
  activeEdge: string | null;
  selectEdge: (edge: SelectableEdge | null) => void;
  // --- the magic go-faster button (Chrome's post-quantum WebRTC key agreement) ---
  // The Chrome major version, or null when this browser is unsupported or new
  // enough that the flag is gone. Null hides the feature entirely.
  pqChromeMajor: number | null;
  // What to display. Combines the live measurement — derived from recent calls'
  // handshake byte counts, never stored — with whether the SDR has been through the
  // dialog, which is stored. The marker only ever suppresses or softens; the
  // measurement decides what is shown.
  pqUi: PqUi;
  // Records that they went through the dialog. Does NOT claim the fix worked.
  markPqApplied: () => void;
  // The SDR opened the instructions. Separates "never saw it" from "saw it and
  // didn't act", which need different follow-ups.
  reportPqOpened: () => void;
}

export interface UseTwilioPhoneOpts {
  enabled: boolean;
  // The Voice token the BFF pushed over the WS (null => no device, controls
  // disabled). Changing the token string refreshes the device in place.
  voiceToken: VoiceToken | null;
  // Called when the SDK signals the token is about to expire — the caller sends
  // a refresh request over the WS and a new voiceToken arrives.
  onRefreshNeeded: () => void;
  // Called when the silent-voicemail heuristic trips and the hook raises the banner
  // (see cockpit/voicemail.ts). The call is not dropped; the caller reports telemetry.
  // Optional.
  onVoicemailPromptRaised?: () => void;
  // The mutual-silence gate emitted a receipt (cockpit/silence-gate.ts). The caller
  // forwards it to the BFF and, for `cut`, ends the call through the normal
  // endCall('gate') path — the hook itself never hangs up. Optional.
  onSilenceGate?: (event: SilenceGateEmit) => void;
  // The softphone hit a device/call error. Reported so a frontend fault is attributable
  // instead of disappearing into 'the far end went away'.
  onClientError?: (code: number | null) => void;
  // The SDR's own voice on a live call. The only evidence of a person that survives a
  // conversation they are conducting hands-free, so without it a good listener with
  // still hands reads as an empty chair.
  onLocalAudible?: () => void;
  // The card on screen for the active call, or null. Carries both signals the hook
  // needs: a card being up un-mutes the blip mute, and a card whose AMD verdict is
  // confirmed stops the silent-voicemail guard. The card rather than two booleans,
  // which would be transposable at the call site.
  currentCall: LeadCall | null;
  // Asked ONCE PER RING, not read as a value: the answer depends on how long this tab
  // has been seatless, which is only knowable at the moment the call arrives (see
  // cockpit/inbound-guard.ts). Required — an omitted rule would silently answer
  // everything, and would report as though the rule had run.
  decideInbound: () => InboundDecision;
  // Whether the SDR currently holds a pool seat. Gates the self-healing REBUILD
  // escalation only: a seatless tab (pool picker) still gets the free recovery
  // arms — wake token refresh and the watchdog's register() retry — but spends
  // none of the capped rebuild budget, so a tab parked on the picker with an
  // unfixable environment (VPN blocking Twilio, say) cannot latch the cap
  // before the SDR ever joins a pool. Required for the same reason as
  // decideInbound: a defaulted value would silently disable the escalation.
  seated: boolean;
}

// What the silence gate did, minus the addressing (useCockpit stamps the callId from
// its own live-call state). Derived rather than restated: a hand-copied shape drifts
// silently, because a field the frame gains but this type does not is one the hook
// simply cannot emit — and nothing anywhere fails to compile.
export type SilenceGateEmit = Omit<
  Extract<CockpitClientFrame, { kind: 'silence_gate' }>,
  'kind' | 'callId'
>;

// Pinned to the two values the receipt accepts. The type says the DOM cannot return
// anything else, but this rides a frame the server validates as a whole: an engine that
// disagreed would not just lose the field, it would sink the `cut` receipt carrying it —
// and that receipt is what ends the seat. Telemetry must not be able to do that.
const tabVisibility = (): 'visible' | 'hidden' =>
  document.visibilityState === 'visible' ? 'visible' : 'hidden';

const MIN_OUTPUT_VOLUME = 0.2; // never fully mute the speaker
const clampVolume = (v: number): number => Math.min(1, Math.max(MIN_OUTPUT_VOLUME, v));

// --- Async-AMD blip mute (feature-flagged) ----------------------------------
// Async AMD bridges the contact the instant they pick up, so on a voicemail the SDR
// hears the greeting until Twilio's machine verdict hangs the call up (the "blip").
// When enabled, the far side is silenced from accept until a card lands.
//
// The un-mute keys off the card, not `confirmed`: waiting for the verdict would
// silence a real human for seconds on every call. Only the speaker is muted, so the
// worst case is a briefly silent opening, never a dropped call.
const BLIP_MUTE_ENABLED = import.meta.env.VITE_AMD_BLIP_MUTE === 'true';
// Why a blip window closed. 'cap' is the one that matters: the speaker opened with
// no card, so the SDR is hearing someone they cannot identify.
type BlipUnmuteReason = 'card' | 'cap' | 'call_ended';
// Safety cap: if no card ever lands, un-mute anyway so a live human can't be
// stranded in silence.
const BLIP_MUTE_MAX_MS = ((): number => {
  const raw = Number(import.meta.env.VITE_AMD_BLIP_MUTE_MAX_MS);
  return Number.isFinite(raw) && raw > 0 ? raw : 8000;
})();

// Remember the SDR's chosen mic/speaker across sessions so an unplug-then-replug
// (or a fresh login) restores their headset instead of Chrome's silent fallback
// to the built-in/"default" device.
const LS_INPUT = 'maestro.twilio.inputDeviceId';
const LS_OUTPUT = 'maestro.twilio.outputDeviceId';
const readPref = (k: string): string | null => {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
};
const writePref = (k: string, v: string): void => {
  try {
    localStorage.setItem(k, v);
  } catch {
    // ignore (private mode)
  }
};
const clearPref = (k: string): void => {
  try {
    localStorage.removeItem(k);
  } catch {
    // ignore (private mode)
  }
};

// Read AND persisted at import. Sign-in is a full document navigation out to the
// provider and back to `/` with no search string, so a parameter only kept in memory
// would be lost for the unauthenticated SDR following a handed-out link — exactly the
// person being armed. Persisting here, before any of that, is what makes the arm
// survive it. Writing once also fixes the stamp: doing it per use would slide the TTL
// forward on every Device rebuild and the arm would never expire.
const URL_ARM = edgeParamOf(typeof window === 'undefined' ? '' : window.location.search);
if (URL_ARM.kind === 'set') {
  writePref(LS_EDGE, encodeArm({ edge: URL_ARM.edge, source: 'url' }, Date.now()));
} else if (URL_ARM.kind === 'clear') {
  // A record, not a deletion: deleting falls through to the geographic default, so
  // the reset link would put the only people worth resetting straight back. It is
  // also the only lever anyone has on an SDR's browser from a distance.
  writePref(LS_EDGE, encodeArm({ edge: null, source: 'url' }, Date.now()));
}

// Constant for the tab, like PQ_CHROME_MAJOR below. `resolvedOptions()` throws on a
// browser with a corrupt or absent ICU database, and this runs at import, where
// there is no boundary to catch it.
const LOCAL_TIME_ZONE: string | null = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
  } catch {
    return null;
  }
})();

const GEO_EDGE: SelectableEdge | null = defaultEdgeFor(LOCAL_TIME_ZONE);

// Null means "no record stored", which is NOT a record holding a null edge — that is
// an SDR who chose automatic, and it suppresses the geographic default.
const resolveArm = (): Arm | null => decodeArm(readPref(LS_EDGE), Date.now());

// Every call site goes through here, so the Device, the preflight and the picker
// cannot disagree about what this session is on.
const resolvePref = (): ResolvedEdge => resolveEdge(resolveArm(), GEO_EDGE);

// `URL_ARM` is frozen for the page, so an unlatched report would repeat on every
// Device rebuild — and StrictMode double-mounts, so even a clean boot logs twice.
let edgeReported = false;

// The browser cannot change under a running tab, so this is genuinely constant.
// (The applied marker is NOT — it changes when the SDR clicks through the dialog,
// so it lives in state, not here.)
const PQ_CHROME_MAJOR = detectChrome(
  typeof navigator === 'undefined' ? '' : navigator.userAgent,
  typeof navigator === 'undefined'
    ? null
    : (
        navigator as Navigator & {
          userAgentData?: { brands?: { brand: string; version: string }[] };
        }
      ).userAgentData,
);

interface TwilioErrorLike {
  message?: string;
  // Twilio's numeric error code (31xxx signalling / 53xxx media). Only the code is reported
  // upstream: the message is free prose and echoes call details for some error shapes, and
  // it would land in a column reporting reads.
  code?: number;
}

// Just the RTCSample fields we grade on (avoids importing the SDK's internal type).
interface SampleLike {
  rtt?: number;
  mos?: number | null;
  packetsLostFraction?: number;
  // Bytes received in the last second. The first sample above zero is the moment RTP is
  // genuinely ARRIVING — not merely that a remote MediaStream object exists, which
  // happens earlier and would overstate how quickly the SDR could hear anything.
  bytesReceived?: number;
  // opus | pcmu. Cheap to carry and it distinguishes a renegotiated/fallback path.
  codecName?: string;
}

function deriveCallQuality(s: SampleLike): CallQuality {
  if (typeof s.mos === 'number') {
    if (s.mos >= 4.2) return 'excellent';
    if (s.mos >= 3.9) return 'good';
    return 'degraded';
  }
  const rtt = typeof s.rtt === 'number' ? s.rtt : 0;
  const loss = typeof s.packetsLostFraction === 'number' ? s.packetsLostFraction : 0;
  if (rtt < 200 && loss < 0.03) return 'excellent';
  if (rtt < 400 && loss < 0.08) return 'good';
  return 'degraded';
}

export function useTwilioPhone({
  enabled,
  voiceToken,
  onRefreshNeeded,
  onVoicemailPromptRaised,
  onSilenceGate,
  onClientError,
  onLocalAudible,
  currentCall,
  decideInbound,
  seated,
}: UseTwilioPhoneOpts): TwilioPhone {
  // The second implies the first; only the second is safe to stop guarding on.
  const cardShown = currentCall != null;
  const humanConfirmed = currentCall?.confirmed === true;
  const [status, setStatus] = useState<TwilioStatus>('idle');
  const [awaitingRemote, setAwaitingRemote] = useState(false);

  // --- the magic go-faster button ---
  // Recent per-call handshake byte counts, newest last. A ref because only the
  // settled verdict drives a render; the samples themselves are bookkeeping.
  const pqSamples = useRef<number[]>([]);
  const [pqVerdict, setPqVerdict] = useState<VerdictState>(newVerdictState);
  // Lazy initializer, not module scope: this changes when the SDR clicks through
  // the dialog, and module scope evaluates once at import — before login, shared
  // across mounts, and unable to re-render anything.
  const [pqApplied, setPqApplied] = useState<boolean>(() => decodeApplied(readPref(LS_PQ_APPLIED)));
  const markPqApplied = useCallback((): void => {
    writePref(LS_PQ_APPLIED, encodeApplied(Date.now()));
    setPqApplied(true);
    logCall('pq_applied', { event: 'cockpit_pq_applied', chrome_major: PQ_CHROME_MAJOR });
  }, []);
  // Latched per state, because this is read during render and StrictMode
  // double-mounts. Without these, a flat rollout is indistinguishable from "nobody
  // saw it", "saw it and ignored it", and "did it and it didn't work" — and those
  // need completely different follow-ups.
  const pqReported = useRef<Set<string>>(new Set());
  const reportPqState = useCallback((ui: PqUi, verdict: PqVerdict, applied: boolean): void => {
    // Latched on the UI/verdict PAIR, not the UI alone: the UI now collapses
    // 'fixed' and 'unknown' into the same 'offer', and latching on what is shown
    // would throw away the distinction the threshold work still needs.
    const key = `${ui}:${verdict}`;
    if (ui === 'hidden' || pqReported.current.has(key)) return;
    pqReported.current.add(key);
    logCall('pq_state', {
      event: 'cockpit_pq_state',
      pq_ui: ui,
      // Still recorded even though nothing surfaces it. This is the population
      // that would let the success state come back: without it we would have to
      // re-instrument before we could re-enable, and the data would start empty.
      pq_verdict: verdict,
      pq_applied: applied,
      chrome_major: PQ_CHROME_MAJOR,
      pq_samples: pqSamples.current.length,
      pq_bytes_median: medianOf(pqSamples.current),
    });
  }, []);
  const reportPqOpened = useCallback((): void => {
    logCall('pq_opened', { event: 'cockpit_pq_opened', chrome_major: PQ_CHROME_MAJOR });
  }, []);
  const pqUiState: PqUi = PQ_CHROME_MAJOR == null ? 'hidden' : pqUi(pqVerdict.verdict);
  useEffect(() => {
    reportPqState(pqUiState, pqVerdict.verdict, pqApplied);
  }, [pqUiState, pqVerdict.verdict, pqApplied, reportPqState]);
  const [humanUnconfirmed, setHumanUnconfirmed] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const [micSilence, setMicSilence] = useState<{ source: MicSilenceSource } | null>(null);
  const [outputVolume, setOutputVolumeState] = useState(1);
  const [volumeSupported, setVolumeSupported] = useState(false);
  const [quality, setQuality] = useState<CallQuality>('unknown');
  const [warnings, setWarnings] = useState<string[]>([]);
  const [deviceRtt, setDeviceRtt] = useState<number | null>(null);
  const [inputDevices, setInputDevices] = useState<MediaDeviceInfo[]>([]);
  const [outputDevices, setOutputDevices] = useState<MediaDeviceInfo[]>([]);
  const [currentInputId, setCurrentInputId] = useState<string | null>(null);
  const [currentOutputId, setCurrentOutputId] = useState<string | null>(null);
  const [outputSelectionSupported, setOutputSelectionSupported] = useState(false);
  const [deviceAlert, setDeviceAlert] = useState<string | null>(null);
  // A ref: written from promise callbacks all over the hook, and only its verdict
  // belongs on screen.
  const micHealth = useRef<MicHealth>(newMicHealth());
  const [micVerdict, setMicVerdict] = useState<MicState>('pending');
  const [micPermission, setMicPermission] = useState<MicPermission>('unknown');
  const [micRetried, setMicRetried] = useState(false);
  const micTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const micRetryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Set while the mic has been lent to something else in this tab. Every automatic
  // path that would re-take it stands down while it is set: none of them can tell a
  // deliberate release from a lost device, and re-taking it would both break
  // whatever is using it and stamp a failure that never happened.
  const micLentOut = useRef(false);
  // Unwinds an acquire that was in flight when the mic was lent out. Not an Error:
  // nothing failed, and every reporting path keys off this identity to say so.
  const LENT_OUT = useRef(Symbol('mic lent out')).current;
  // Throttles the opportunistic re-acquire. One physical plug fires several
  // devicechange events, and each one would otherwise be a getUserMedia and an error log.
  const lastAcquireAt = useRef(0);
  // Held so it cannot be collected — a dropped PermissionStatus stops firing onchange.
  const micPermissionStatus = useRef<PermissionStatus | null>(null);

  const deviceRef = useRef<Device | null>(null);
  const callRef = useRef<Call | null>(null);
  // The mic deviceId we last asked for — to detect when it's the one that vanished.
  const selectedInputIdRef = useRef<string | null>(null);
  // Level refs sampled by the wave meters in rAF (not state — ~60Hz events).
  const inputLevelRef = useRef(0);
  const outputLevelRef = useRef(0);
  // Volume mirrored into a ref so the rAF accessors / event handlers read the
  // current value without a stale closure.
  const outputVolumeRef = useRef(1);
  const inputWired = useRef(false); // attach the inputVolume listener once
  const lastAudioKey = useRef<string | null>(null); // dedupes mic snapshot reporting
  // Output-volume Web Audio graph (the ONLY audio we create) — lazily built on
  // the first user volume gesture so we never reroute call audio through a
  // suspended AudioContext (which would silence it). Until then Twilio plays the
  // element directly at full volume.
  const audioCtxRef = useRef<AudioContext | null>(null);
  const gainRef = useRef<GainNode | null>(null);
  const audioElRef = useRef<HTMLAudioElement | null>(null);
  const wrappedEls = useRef<WeakSet<HTMLAudioElement>>(new WeakSet());
  // Async-AMD blip mute (see BLIP_MUTE_* above): a per-call one-shot timer and a
  // flag tracking whether the far-side speaker is currently silenced for the blip
  // window. `blipMuting` lets the `audio` handler re-assert the mute if the call's
  // playback element arrives AFTER accept (it does), and lets setOutputVolume /
  // un-mute know not to fight each other.
  const blipMuteTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const blipMuting = useRef(false);
  // Blip-window telemetry. The core stamps `call_events.carded` the instant IT hands
  // the card to the cockpit layer, with no acknowledgement from here — so a card that
  // never reaches the browser is indistinguishable in ClickHouse from one the SDR read.
  // These three refs let the SPA report its own side of that handoff: when the window
  // opened, whether the safety cap closed it (the SDR was un-muted with NO card, which
  // is worse than silence — they hear a stranger with no idea who), and the elapsed ms.
  const blipMutedAtRef = useRef<number | null>(null);
  const blipCallSidRef = useRef<string | null>(null);
  // Mirror the props: the `accept` handler and the voicemail timer are armed at
  // accept and read seconds later, when a closure-captured prop is stale.
  const cardShownRef = useRef(cardShown);
  cardShownRef.current = cardShown;
  const humanConfirmedRef = useRef(humanConfirmed);
  humanConfirmedRef.current = humanConfirmed;
  // Mirror the prompt flag into a ref: the `volume` handler (below) clears the prompt
  // on late far-side audio and reads this to know a prompt is up without re-subscribing.
  const humanUnconfirmedRef = useRef(humanUnconfirmed);
  humanUnconfirmedRef.current = humanUnconfirmed;
  // --- the mutual-silence gate (cockpit/silence-gate.ts) ---------------------
  // Per-card state; the config rides the card itself (lead_assigned.silenceGate),
  // so each call runs under the settings in force when it was pushed. Mirrored
  // into refs for the once-wired volume handler, like everything above.
  const [silencePrompt, setSilencePrompt] = useState<{
    deadlineAt: number | null;
    rule: SilenceRule;
  } | null>(null);
  const silenceGateState = useRef<SilenceGateState>(newSilenceGate());
  const silenceCfg = currentCall?.silenceGate ?? null;
  const silenceCfgRef = useRef(silenceCfg);
  silenceCfgRef.current = silenceCfg;
  const silenceCutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onSilenceGateRef = useRef(onSilenceGate);
  onSilenceGateRef.current = onSilenceGate;
  const clearSilenceCutTimer = useCallback((): void => {
    if (silenceCutTimer.current != null) {
      clearTimeout(silenceCutTimer.current);
      silenceCutTimer.current = null;
    }
  }, []);
  // Dismiss a raised prompt and emit
  // the `stayed` receipt. Stable identity — used inside the once-wired handlers.
  const dismissSilenceGate = useCallback(
    (how: 'clicked' | 'sound' | 'disposed'): void => {
      const gs = silenceGateState.current;
      if (!isRaised(gs)) return;
      markDismissed(gs, performance.now());
      clearSilenceCutTimer();
      if (active.current) setSilencePrompt(null);
      onSilenceGateRef.current?.({
        action: 'stayed',
        mode: gateMode(silenceCfgRef.current),
        ...(gs.firedRule ? { rule: gs.firedRule } : {}),
        how,
        reactionMs: reactionMs(gs) ?? 0,
      });
    },
    [clearSilenceCutTimer],
  );
  const staySilenceGate = useCallback(
    (): void => dismissSilenceGate('clicked'),
    [dismissSilenceGate],
  );
  // Arm the gate per CARD, keyed on callId (a lead_confirmed upgrade replaces the
  // card object but is the same call and must not reset a running quiet clock).
  // The floor measures card time — the SDR cost being bought back — so the state
  // is born when the card appears and dies with it.
  const silenceCallIdRef = useRef<string | null>(null);
  useEffect(() => {
    const callId = currentCall?.callId ?? null;
    if (callId === silenceCallIdRef.current) return;
    silenceCallIdRef.current = callId;
    silenceGateState.current = newSilenceGate(callId == null ? null : performance.now());
    clearSilenceCutTimer();
    setSilencePrompt(null);
  }, [currentCall?.callId, clearSilenceCutTimer]);
  // A real `human` verdict landing after the prompt was raised clears it — the guard
  // would never have fired for a confirmed human, so honour a late confirmation too.
  useEffect(() => {
    if (humanConfirmed) setHumanUnconfirmed(false);
  }, [humanConfirmed]);
  // `active` gates all async event handlers so a late callback after teardown
  // can't set state on a torn-down device. `onRefresh` is a ref so the device
  // effect needn't depend on (and rebuild for) a changing callback identity.
  const active = useRef(false);
  const onRefresh = useRef(onRefreshNeeded);
  onRefresh.current = onRefreshNeeded;
  // --- Device self-healing (policies in device-rebuild.ts) -------------------
  // A wedged Device — dead transport, silent unregistration, the SDK's ICE
  // crash — is unrecoverable in place; only a fresh `new Device` (what a manual
  // tab refresh achieves) brings the seat back. The nonce re-runs the token
  // effect so a rebuild goes through the SAME build path as first construction:
  // nothing about a rebuilt phone is special-cased.
  const [rebuildNonce, setRebuildNonce] = useState(0);
  // The cap gets its OWN state rather than riding `deviceAlert`: the mic paths
  // legitimately overwrite/clear that slot on devicechange, and the two facts
  // ("recovery gave up" / "your headset vanished") can both be true at once —
  // the UI renders both rather than whichever wrote last.
  const [rebuildCapped, setRebuildCapped] = useState(false);
  const rebuildTimesRef = useRef<number[]>([]);
  const err31005TimesRef = useRef<number[]>([]);
  // A rebuild wanted during a live call waits here until the call ends: media
  // that is working right now outranks a policy that says it shouldn't be.
  const pendingRebuildRef = useRef<PendingRebuild | null>(null);
  // Backoff for token re-asks while none is usable — see TOKEN_REASK_* in
  // device-rebuild.ts. Reset the moment a real token lands.
  const tokenAskRef = useRef({ nextAt: 0, delayMs: TOKEN_REASK_BASE_MS });
  // Latched when the hourly cap trips so the cap is logged once, not per tick.
  const rebuildCappedRef = useRef(false);
  const watchdogRef = useRef<WatchdogState>({ breachAt: null, registerTriedAt: null });
  const lastWakeRefreshAtRef = useRef(0);
  // Set on a 20101 rebuild: the token we hold is the invalid one that CAUSED
  // the error, so the replacement waits for a changed token string instead of
  // predictably re-failing with the same credential. A fresh mint always
  // differs (its exp moves), so the token effect's dep fires the build.
  const awaitingTokenRef = useRef(false);
  // Mirrored like the other per-render props the timer/event handlers read.
  const seatedRef = useRef(seated);
  seatedRef.current = seated;
  // Silent-voicemail guard (cockpit/voicemail.ts): a per-call one-shot timer armed
  // on `accept`, plus the two conditions tracked off `call.on('volume')` — did the
  // far side ever produce audio, and did we. `onPromptRaised` is a ref so the device
  // effect needn't rebuild when the callback identity changes.
  const onPromptRaised = useRef(onVoicemailPromptRaised);
  onPromptRaised.current = onVoicemailPromptRaised;
  // A ref for the same reason as onPromptRaised: the device/call handlers are wired once and
  // must not re-subscribe when the callback identity changes.
  const onClientErrorRef = useRef(onClientError);
  onClientErrorRef.current = onClientError;
  const onLocalAudibleRef = useRef(onLocalAudible);
  onLocalAudibleRef.current = onLocalAudible;
  // A ref for the same reason: the `incoming` handler is wired once, inside the device
  // effect, and must ask the CURRENT rule at ring time rather than one closed over at
  // registration — the tab's idleness is a different answer minutes later.
  const decideInboundRef = useRef(decideInbound);
  decideInboundRef.current = decideInbound;
  const vmTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const vmRemoteHeard = useRef(false);
  const vmLocalSpoke = useRef(false);
  // Two-sided audio facts for the WHOLE call, distinct from the vm* refs above, which
  // the guard clears at the end of its 10s window. This is what answers "was there ever
  // a conversation, and could the SDR hear it" (cockpit/audio-liveness.ts).
  const liveness = useRef(newAudioLiveness());
  // Per-call network summary, folded from the ~1Hz RTC sample stream. Those samples
  // already drive the quality badge and are then dropped, so nothing records what the
  // SDR's path actually looked like on a call they complained about — and media setup
  // over a distant, lossy link is a leading explanation for the SDR joining late.
  const net = useRef({
    samples: 0,
    rttSum: 0,
    rttMax: 0,
    mosMin: null as number | null,
    lossMax: 0,
    // Accept → the first second in which RTP actually arrived. Paired with
    // `first_remote_audible_ms` this splits the two reasons an SDR hears nothing: media
    // that took seconds to establish (this moves) versus a lead who had not spoken yet
    // (this stays small while audible moves). One number alone carries both and answers
    // neither.
    firstMediaMs: null as number | null,
    // Accept → the first `volume` event of ANY value, zero included. The SDK wires
    // `_mediaHandler.onvolume` straight to this event (call.js), so the first one fires
    // once the remote MediaStream is ATTACHED and its analyser starts.
    //
    // NOT evidence the media path is up, despite what this comment used to claim. The SDK
    // calls `_startPollingVolume()` from the peer connection's `ontrack`
    // (peerconnection.js:547), which fires during setRemoteDescription — before ICE,
    // before DTLS, before any packet. Production reads ~25ms, min 5ms, which is the
    // analyser starting and nothing else. Read it as "the track arrived", and read the
    // `setup_*` fields for when media could actually flow.
    //
    // It replaced a `firstSampleMs` taken from the `sample` event, which fires on a 1Hz
    // timer and so read ~1005ms for every SDR on every continent. Two fields in a row
    // that timed something adjacent to the question — hence the peer-connection probe.
    firstVolumeMs: null as number | null,
    codec: null as string | null,
  });
  const warningsRef = useRef<Set<string>>(new Set());
  // The opening observation is reported from whichever comes first: the far side being
  // heard (which CANCELS the voicemail timer) or the window expiring. Reporting only
  // from the timer body missed every healthy call, so `remote_heard` would have been
  // false in every row and the denominator this exists for would not exist.
  const openingReported = useRef(false);
  // Deliberately OUTSIDE `net.current`, which resetNet() wipes at accept and again at
  // call end — the probe has to outlive both to detach cleanly.
  const setupTiming = useRef<SetupTiming | null>(null);
  const detachSetup = useRef<(() => void) | null>(null);
  // When accept() was called, versus when the `accept` event fired. That window is local
  // SDP work (the mic was acquired back at `registered`), so it should be tens of ms —
  // and if it is not, nothing else on the row would show it.
  const acceptInvokedAt = useRef<number | null>(null);
  // Stamped when the Device is built, so the report says what THIS Device asked for.
  // Re-resolving at report time could disagree with the live connection if the arm
  // expired mid-call.
  const armedEdgeRef = useRef<SelectableEdge | null>(null);
  const armedSourceRef = useRef<EdgeSource | null>(null);
  // Mirrored so the picker re-renders. One piece of state rather than two: the edge
  // and how it was chosen drive different parts of the same row and must not drift
  // apart. Read once on mount — a Device rebuild does not change what is stored.
  const [edgePref, setEdgePref] = useState<ResolvedEdge>(resolvePref);
  // `undefined` = no Device has been built yet, which is NOT the same as "built with
  // no pin" (null). Collapsing the two is what makes a pending prompt appear at boot.
  const [armedEdge, setArmedEdge] = useState<SelectableEdge | null | undefined>(undefined);
  const [activeEdge, setActiveEdge] = useState<string | null>(null);
  const resetNet = useCallback((): void => {
    net.current = {
      samples: 0,
      rttSum: 0,
      rttMax: 0,
      mosMin: null,
      lossMax: 0,
      firstMediaMs: null,
      firstVolumeMs: null,
      codec: null,
    };
    warningsRef.current = new Set();
  }, []);

  // Rounded at the edge so the log carries numbers a query can bucket, and `null`
  // (never 0) for anything the browser never reported.
  const netReport = useCallback(
    () => ({
      rtt_avg_ms:
        net.current.samples > 0 ? Math.round(net.current.rttSum / net.current.samples) : null,
      rtt_max_ms: net.current.rttMax > 0 ? Math.round(net.current.rttMax) : null,
      mos_min: net.current.mosMin,
      packet_loss_max: net.current.lossMax > 0 ? Number(net.current.lossMax.toFixed(4)) : null,
      rtc_samples: net.current.samples,
      // Accept → RTP arriving. Large here means the path took that long to establish;
      // small here with a large `first_remote_audible_ms` means the media was fine and
      // the lead simply had not spoken. Null when no RTP ever arrived at all, which is
      // its own answer.
      first_media_ms: net.current.firstMediaMs,
      // Accept → the remote track was attached (first volume event, silence included).
      // This is NOT "the media path was up" — it fires at `ontrack`, before ICE and DTLS,
      // and reads ~25ms on every call. The decomposition the ~3s floor needs is in the
      // `setup_*` fields below; this is kept only as the track-arrival marker.
      first_volume_ms: net.current.firstVolumeMs,
      codec: net.current.codec,
      // Which Twilio edge this Device is attached to. `roaming` picks by latency and
      // there is no African edge, so this is what says whether a slow SDR is simply far
      // from their edge or has been placed on the wrong one. Never logged before, and it
      // is the first thing you would want when RTT splits the fleet in two.
      edge: deviceRef.current?.edge ?? null,
      // What we ASKED for, against `edge` above (what Twilio attached to): alone,
      // `edge` cannot separate an honoured request from an ignored one, or from a
      // fallback to the chain's terminal `roaming`. NOT named `selected_edge` — that
      // key already exists on the preflight log meaning Twilio's own selection, and
      // one facet with two opposite meanings would quietly mix populations.
      requested_edge: armedEdgeRef.current,
      arm_source: armedSourceRef.current,
      // What the SDR's REGION would have given them, and the zone that decided it.
      // Without these a session the geographic rule missed — an SA laptop left on UTC
      // — is indistinguishable from one in London, and so is an SDR who opted out:
      // both report a null edge and a null source. Coverage is otherwise unmeasurable
      // after the fact, which is the failure mode a geographic rule actually has.
      geo_edge: GEO_EDGE,
      time_zone: LOCAL_TIME_ZONE,
      // Twilio's own warnings (high-rtt, high-jitter, low-mos, constant-audio-*). Named
      // rather than counted: which one fires says where the path is failing.
      warnings: [...warningsRef.current],
      // The phase split: gathering / connectivity checks / DTLS / first packet / first
      // packet carrying audio, all from the same accept origin as the fields above.
      // Every `setup_*` key comes from this one call, so the module's key-set test covers
      // the whole namespace — including `setup_accept_ms`, which used to be built here
      // and was the one field that test could not protect.
      ...setupReport(setupTiming.current),
    }),
    [],
  );
  // The card's ids, mirrored so the call-scoped handlers (wired once, at device setup)
  // can stamp them without re-subscribing. Null until the card lands — which is exactly
  // the window one-way audio lives in, so `call_sid` is the identifier that spans it.
  const callIdRef = useRef<string | null>(null);
  const poolIdRef = useRef<string | null>(null);
  useEffect(() => {
    callIdRef.current = currentCall?.callId ?? null;
    poolIdRef.current = currentCall?.poolId ?? null;
  }, [currentCall]);
  // Every audio event carries the same identity block. `call_sid` is the join key to
  // ClickHouse `call_events.call_sid`, and is known from the ring; `call_id` only once
  // the card has landed.
  // Set from the leg's own custom parameter, which arrives with the call itself.
  // `callIdRef` comes from the CARD, so it is null on every pre-card path — accept, the
  // blip mute, the no-card cap — which is exactly the dead-air population these logs
  // exist to size. `call_sid` cannot stand in: <Dial><Client> mints a new call, so the
  // browser's CallSid is the CHILD leg's and never matches `call_events.call_sid`.
  const legCallIdRef = useRef<string | null>(null);
  const audioIds = useCallback(
    () => ({
      call_sid: blipCallSidRef.current,
      call_id: legCallIdRef.current ?? callIdRef.current,
      pool_id: poolIdRef.current,
    }),
    [],
  );

  // What the opening looked like: who made a sound in the guard's window, and whether it
  // read as a silent voicemail. Emitted once per call, for EVERY call — the healthy
  // population is the denominator every rate here needs.
  const reportOpening = useCallback((): void => {
    if (openingReported.current) return;
    openingReported.current = true;
    const observation = {
      remoteHeard: vmRemoteHeard.current,
      localSpoke: vmLocalSpoke.current,
      humanConfirmed: humanConfirmedRef.current,
    };
    logCall('call_opening', {
      event: 'cockpit_call_opening',
      ...audioIds(),
      remote_heard: observation.remoteHeard,
      local_spoke: observation.localSpoke,
      human_confirmed: observation.humanConfirmed,
      voicemail_prompt: isVoicemail(observation),
      // The thresholds this was judged against — build-time env, so a report without
      // them cannot be compared across deploys.
      window_ms: voicemailConfig.windowMs,
      remote_max: voicemailConfig.remoteMax,
      local_min: voicemailConfig.localMin,
    });
  }, [audioIds]);
  // UI mirror of "far side hasn't spoken yet" (one-shot per call, ref-guarded so
  // the ~60Hz volume stream sets React state at most once). See `awaitingRemote`.
  const awaitingRemoteRef = useRef(false);
  // Stable (refs only) so it can be used inside the device effect + teardown
  // without rebuilding them.
  const clearVmGuard = useCallback((): void => {
    if (vmTimer.current != null) {
      clearTimeout(vmTimer.current);
      vmTimer.current = null;
    }
    vmRemoteHeard.current = false;
    vmLocalSpoke.current = false;
  }, []);

  // Route the current call's playback element through gain → destination. One
  // wrap per element (createMediaElementSource is one-shot and reroutes output,
  // so it must reach destination or audio goes silent).
  const applyGainTo = useCallback((el: HTMLAudioElement): void => {
    const ctx = audioCtxRef.current;
    if (!ctx) return; // no gesture yet — leave Twilio's direct playback alone
    if (!wrappedEls.current.has(el)) {
      try {
        const src = ctx.createMediaElementSource(el);
        const gain = ctx.createGain();
        src.connect(gain).connect(ctx.destination);
        gainRef.current = gain;
        wrappedEls.current.add(el);
      } catch {
        return; // already wrapped elsewhere / unsupported — keep direct playback
      }
    }
    if (gainRef.current) gainRef.current.gain.value = outputVolumeRef.current;
  }, []);

  // Silence the far-side audio for the blip window. Sets BOTH levers because the
  // call's playback path depends on whether the volume slider was ever touched:
  //   - element.muted handles the common case (Twilio plays the element directly)
  //   - zeroing the gain handles the wrapped case (a touched element flows through
  //     gain → destination, BYPASSING element.muted)
  // Idempotent; safe whether or not the gain graph exists yet.
  const muteRemoteAudio = useCallback((): void => {
    const opening = !blipMuting.current;
    blipMuting.current = true;
    const el = audioElRef.current;
    const gain = gainRef.current;
    if (el) el.muted = true;
    if (gain) gain.gain.value = 0;
    // Report the transition, not the call: the `audio` handler re-asserts the mute
    // when the playback element arrives after accept, and that is the same window.
    // Before the `opening` bail: a re-assert is idempotent here (markMuted keeps the
    // first origin), and the liveness clock must start even if the reporting does not.
    markMuted(liveness.current, performance.now());
    if (!opening) return;
    blipMutedAtRef.current = performance.now();
    const context = {
      ...audioIds(),
      cap_ms: BLIP_MUTE_MAX_MS,
      // Which levers existed to mute. Neither present means the far side was never
      // actually silenced, however the flags below read.
      had_element: el != null,
      had_gain: gain != null,
    };
    rumAction('cockpit.blip.muted', context);
    logCall('blip_muted', { event: 'cockpit_blip_muted', ...context });
  }, [audioIds]);

  // Restore far-side audio after the blip window (or on call end). Returns the
  // gain to the SDR's chosen output volume, not blindly to 1.
  //
  // Reports from INSIDE the state change rather than at the call sites, because the
  // early return and the two null checks below are exactly where a restore can
  // silently not happen — logging next to the call would record an intent, which is
  // the same send-side assumption `call_events.carded` already makes.
  const unmuteRemoteAudio = useCallback(
    (reason: BlipUnmuteReason = 'call_ended'): void => {
      if (blipMuteTimer.current != null) {
        clearTimeout(blipMuteTimer.current);
        blipMuteTimer.current = null;
      }
      if (!blipMuting.current) return;
      blipMuting.current = false;
      const el = audioElRef.current;
      const gain = gainRef.current;
      if (el) el.muted = false;
      if (gain) gain.gain.value = outputVolumeRef.current;
      const mutedAt = blipMutedAtRef.current;
      blipMutedAtRef.current = null;
      // Read BEFORE clearing the mute clock: this is the whole point of the window —
      // whether the lead was talking through it, and from how early.
      const heard = reportLiveness(liveness.current);
      markUnmuted(liveness.current);
      const context = {
        // 'card'       the card landed and opened the speaker — the healthy path
        // 'cap'        BLIP_MUTE_MAX_MS expired with NO card: the SDR now hears a
        //              stranger with nothing on screen saying who. Alert on this.
        // 'call_ended' the call died inside the window; the SDR never heard them
        reason,
        ...audioIds(),
        // How long the SDR could not hear the lead.
        blip_ms: mutedAt == null ? null : Math.round(performance.now() - mutedAt),
        restored_element: el != null,
        restored_gain: gain != null,
        // Direct evidence, not inference: the SDK measures the far side off the remote
        // MediaStream, so these stay truthful while our playback mute is closed.
        one_way: heard.one_way,
        first_remote_while_muted_ms: heard.first_remote_while_muted_ms,
        remote_while_muted_ms: heard.remote_while_muted_ms,
      };
      rumAction('cockpit.blip.unmuted', context);
      // warn for the two states where the SDR could not hear a lead who was talking, so
      // they are separable by level as well as by attribute.
      logCall(
        'blip_unmuted',
        { event: 'cockpit_blip_unmuted', ...context },
        reason === 'cap' || heard.one_way ? 'warn' : 'info',
      );
    },
    [audioIds],
  );

  const setOutputVolume = useCallback(
    (v: number): void => {
      const clamped = clampVolume(v);
      outputVolumeRef.current = clamped;
      setOutputVolumeState(clamped);
      // First gesture: stand up the AudioContext (allowed to resume now) and
      // route the live call element through it.
      if (!audioCtxRef.current) {
        try {
          audioCtxRef.current = new AudioContext();
        } catch {
          // Web Audio unsupported — volume control is a no-op.
        }
      }
      void audioCtxRef.current?.resume();
      if (audioElRef.current) applyGainTo(audioElRef.current);
      // Don't un-silence mid-blip: while the blip mute is active the gain stays 0
      // and this new volume takes effect when the window ends (unmuteRemoteAudio
      // restores outputVolumeRef.current).
      if (gainRef.current && !blipMuting.current) gainRef.current.gain.value = clamped;
    },
    [applyGainTo],
  );

  const playTestTone = useCallback((): void => {
    const audio = deviceRef.current?.audio;
    if (!audio) return; // sim / not registered — inert
    void audioCtxRef.current?.resume();
    void audio.speakerDevices.test().catch(() => {
      // ignore — best-effort bleep
    });
  }, []);

  // --- Connection test (Twilio preflight) -----------------------------------
  // Live calls can only measure the TOTAL wait from accept to audio (~19 round trips,
  // so ~1s at 32ms RTT and ~7s at 360ms). Preflight breaks that into signalling / ICE /
  // DTLS and says whether media is being relayed — and it dials Twilio's echo service,
  // not a lead, so an SDR can run it without fielding a real call.
  const [connectionTest, setConnectionTest] = useState<ConnectionTestState>({
    status: 'idle',
    message: null,
  });
  const runConnectionTest = useCallback((): void => {
    const token = voiceToken?.token;
    // No token means no Device and nothing to test against. Refuse rather than
    // reporting a failure the SDR cannot act on.
    if (!token) {
      setConnectionTest({ status: 'failed', message: 'Not connected — try again in a moment.' });
      return;
    }
    setConnectionTest({ status: 'running', message: 'Testing your connection…' });
    try {
      // Same codec order as the live Device below. The SDK's preflight default is
      // ['pcmu','opus'] — the reverse — so without this the test would measure a media
      // path the SDR's real calls never take.
      // Prefer what the live Device actually asked for: if the arm expired since it
      // was built, re-resolving here would test roaming while every real call still
      // runs pinned — the mismatch this option exists to prevent.
      const preflightEdge = deviceRef.current ? armedEdgeRef.current : resolvePref().edge;
      const test = Device.runPreflight(token, {
        codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
        ...preflightEdgeOptions(preflightEdge),
      });
      test.on('completed', (report: PreflightReportLike) => {
        const log = buildPreflightLog(report);
        logCall('preflight', log, log.turn_required === true ? 'warn' : 'info');
        rumAction('cockpit.preflight.completed', { edge: log.edge, turn: log.turn_required });
        if (active.current) setConnectionTest({ status: 'done', message: preflightSummary(log) });
      });
      test.on('failed', (err: TwilioErrorLike) => {
        // Reported, not swallowed: a preflight that cannot complete is itself a finding
        // about this SDR's network, and it is the population we most need to hear from.
        logCall(
          'preflight_failed',
          {
            event: 'cockpit_preflight_failed',
            code: typeof err?.code === 'number' ? err.code : null,
            // The success path carries the edge via the report; without it here, an
            // edge-specific failure is indistinguishable from an unarmed one. That
            // matters more than it sounds: preflight currently fails for everyone at
            // Twilio's gateway, so this path is the ONLY preflight data there is.
            requested_edge: preflightEdge,
          },
          'warn',
        );
        if (active.current) {
          setConnectionTest({ status: 'failed', message: 'Test could not complete — reported.' });
        }
      });
    } catch {
      setConnectionTest({ status: 'failed', message: 'Test could not start — reported.' });
    }
  }, [voiceToken?.token]);

  // Writes the preference only; the live Device keeps its edge until a reload.
  // "Automatic" writes a record rather than deleting one — deleting would re-derive
  // the geographic default they just turned off.
  const selectEdge = useCallback((edge: SelectableEdge | null): void => {
    writePref(LS_EDGE, encodeArm({ edge, source: 'picker' }, Date.now()));
    setEdgePref({ edge, source: edge ? 'picker' : null });
  }, []);

  const getInputLevel = useCallback(() => inputLevelRef.current, []);
  const getOutputLevel = useCallback(() => outputLevelRef.current, []);

  // Publish the SDK's current device lists + selections into React state.
  const refreshDevices = useCallback((): void => {
    const audio = deviceRef.current?.audio;
    if (!audio || !active.current) return;
    setInputDevices([...audio.availableInputDevices.values()]);
    setOutputDevices([...audio.availableOutputDevices.values()]);
    setCurrentInputId(audio.inputDevice?.deviceId ?? null);
    const spk = [...audio.speakerDevices.get()];
    setCurrentOutputId(spk[0]?.deviceId ?? null);
  }, []);

  // Wakes exactly once more, at the next threshold. A timeout, never an interval:
  // a blocked episode lasts hours, and polling would re-render the cockpit tree every
  // second of it.
  const refreshMicVerdict = useCallback((): void => {
    if (micTimer.current) clearTimeout(micTimer.current);
    micTimer.current = null;
    const h = micHealth.current;
    const now = Date.now();
    const verdict = micState(h, now);
    if (!active.current) return;
    setMicVerdict(verdict);
    setMicRetried(h.retried);
    // `blocked` is terminal until the health record changes; `live` needs no clock.
    const since = micEpisodeStart(h);
    if (since === null || verdict === 'live' || verdict === 'blocked') return;
    micTimer.current = setTimeout(
      refreshMicVerdict,
      since + (verdict === 'pending' ? WARN_MS : BLOCK_MS) - now,
    );
  }, []);

  const stampMicAcquired = useCallback((): void => {
    const h = micHealth.current;
    h.acquiredAt = Date.now();
    h.failedAt = null;
    h.lastError = null;
    h.retried = false;
    refreshMicVerdict();
  }, [refreshMicVerdict]);

  const stampMicFailed = useCallback(
    (err: unknown): void => {
      const h = micHealth.current;
      h.failedAt = Date.now();
      h.lastError = err instanceof Error ? err.message : String(err);
      refreshMicVerdict();
    },
    [refreshMicVerdict],
  );

  // Twilio's own fallback (audiohelper `_onActiveInputChanged`): 'default' is a
  // CHROMIUM pseudo-device. Firefox and Safari never enumerate it, so asking for it by
  // name there rejects deterministically — which, now that a dead mic gates the pool
  // picker, would lock a Safari SDR with a working headset out of dialing entirely.
  const firstInput = (audio: NonNullable<Device['audio']>): MediaDeviceInfo | undefined =>
    audio.availableInputDevices.get('default') ?? [...audio.availableInputDevices.values()][0];

  // Upgrade from the default device to whatever the SDR last chose, where it is
  // still attached. Best effort throughout: a remembered device that has since been
  // unplugged is not a failure.
  const restorePreferredDevices = async (audio: NonNullable<Device['audio']>): Promise<void> => {
    const prefIn = readPref(LS_INPUT);
    if (prefIn && prefIn !== 'default' && audio.availableInputDevices.has(prefIn)) {
      await audio.setInputDevice(prefIn);
    }
    const prefOut = readPref(LS_OUTPUT);
    if (audio.isOutputSelectionSupported && prefOut && audio.availableOutputDevices.has(prefOut)) {
      try {
        await audio.speakerDevices.set(prefOut);
      } catch {
        // ignore
      }
    }
  };

  // Acquire Twilio's SINGLE mic stream, then upgrade to the SDR's remembered devices.
  // Runs on register, on the one retry, on Re-check, and on a device change while
  // banner'd.
  //
  // The getUserMedia probe must stay FIRST: setInputDevice resolves the id against
  // Twilio's enumerateDevices() map and rejects there, before ever reaching
  // getUserMedia, so with permission blocked that map is empty and no retry can raise
  // a prompt. Tracks are stopped at once so Twilio keeps the one stream.
  const acquireMic = useCallback(
    (stage: 'acquire' | 'reacquire' = 'acquire'): void => {
      const audio = deviceRef.current?.audio;
      if (!audio || micLentOut.current) return;
      // A retry already in flight would otherwise land on top of this one.
      if (micRetryTimer.current) clearTimeout(micRetryTimer.current);
      micRetryTimer.current = null;
      const now = Date.now();
      lastAcquireAt.current = now;
      // Only a deliberate attempt — register, or the SDR pressing Re-check — restarts
      // the clock, so a slow one is never read as a dead mic. A retry or an
      // opportunistic re-acquire must NOT: a device that re-enumerates faster than
      // BLOCK_MS would keep resetting the episode and `blocked` would never arrive.
      if (stage === 'acquire') {
        micHealth.current.attemptedAt = now;
        refreshMicVerdict();
      }
      // Started off a resolved promise so a missing `mediaDevices` (insecure origin,
      // older engine) rejects into the catch below instead of throwing synchronously
      // out of an onClick and past every handler that follows this call.
      void Promise.resolve()
        .then(() => navigator.mediaDevices.getUserMedia({ audio: true }))
        .then((stream) => {
          stream.getTracks().forEach((t) => t.stop());
          // The grant does not repopulate Twilio's device map on its own — the SDK
          // refreshes it from its own `devicechange` and permission listeners, which
          // we would be racing. Underscored but published on the SDK's typings.
          return audio._updateAvailableDevices();
        })
        .then(() => {
          // Re-checked here, not only at entry: this chain can have been in flight
          // when the mic was lent out, and taking the device now would put a second
          // capture on it behind the borrower's back.
          if (micLentOut.current) throw LENT_OUT;
          const input = firstInput(audio);
          if (!input) throw new Error('No audio input devices');
          return audio.setInputDevice(input.deviceId);
        })
        .then(() => restorePreferredDevices(audio))
        .then(() => {
          selectedInputIdRef.current = audio.inputDevice?.deviceId ?? null;
          refreshDevices();
          stampMicAcquired();
          if (active.current) setLastError(null);
          // The SDR's STARTING state. This is the earliest point worth reporting:
          // before the mic is acquired both Chromium and Firefox return a single
          // blank {deviceId:'',groupId:'',label:''} entry, so there is literally
          // nothing to see until permission lands here.
          reportAudioSnapshot(
            'acquire',
            [...audio.availableInputDevices.values()],
            audio.inputDevice?.deviceId ?? null,
            lastAudioKey,
          );
        })
        .catch((err: unknown) => {
          // Stood down rather than failed. The borrower hands the mic back itself,
          // and reporting this would invent a fault out of a deliberate loan.
          if (err === LENT_OUT) return;
          if (active.current) {
            setLastError(`microphone: ${err instanceof Error ? err.message : 'unavailable'}`);
          }
          // Distinct stage: the fleet monitor counts first-acquire failures, and a
          // session stuck retrying would otherwise carry a day past its threshold
          // on its own.
          reportAudioFailure(stage, err);
          stampMicFailed(err);
          const h = micHealth.current;
          if (!active.current || h.retried) return;
          h.retried = true;
          micRetryTimer.current = setTimeout(() => acquireMic('reacquire'), RETRY_AFTER_MS);
        });
    },
    [refreshDevices, refreshMicVerdict, stampMicAcquired, stampMicFailed],
  );

  // Switch the mic. Applies to the live call too (Twilio re-acquires the track —
  // no reconnect) and is remembered so the next session/unplug restores it.
  const selectInputDevice = useCallback(
    (deviceId: string): void => {
      const audio = deviceRef.current?.audio;
      if (!audio) return;
      writePref(LS_INPUT, deviceId);
      void audio
        .setInputDevice(deviceId)
        .then(() => {
          // Only once it opened. This ref is read as "the device we are actually on"
          // when the mic has to be re-taken, and a pick that rejected would send that
          // recovery at a device this session never held.
          selectedInputIdRef.current = audio.inputDevice?.deviceId ?? deviceId;
          // Ahead of the active guard below: the mic is held either way.
          stampMicAcquired();
          if (!active.current) return;
          setCurrentInputId(audio.inputDevice?.deviceId ?? deviceId);
          setDeviceAlert(null);
          // Whether an SDR ever picks is the measure of any future UI warning.
          reportAudioSnapshot(
            'user_pick',
            [...audio.availableInputDevices.values()],
            audio.inputDevice?.deviceId ?? deviceId,
            lastAudioKey,
          );
        })
        .catch((err: unknown) => {
          if (active.current)
            setLastError(`mic: ${err instanceof Error ? err.message : 'unavailable'}`);
          reportAudioFailure('user_pick', err);
          stampMicFailed(err);
        });
    },
    [stampMicAcquired, stampMicFailed],
  );

  // The SDR says they fixed it: hand the episode a fresh retry. acquireMic restarts
  // the clock, so the banner cannot flash back mid-attempt.
  const recheckMic = useCallback((): void => {
    if (micRetryTimer.current) clearTimeout(micRetryTimer.current);
    micRetryTimer.current = null;
    micHealth.current.retried = false;
    acquireMic('acquire');
  }, [acquireMic]);

  // Lend the microphone to something else in this tab, and take it back after.
  //
  // Twilio keeps ONE input stream open for metering whenever a device is registered,
  // and two captures of the same device are not reliably the same capture — on
  // Windows a second one is handed its own processing. So a caller that needs the
  // real capture path has to be the only holder while it uses it.
  //
  // A deliberate release is not a mic fault and must never be stamped as one: a
  // stamped failure gates the pool a minute later.
  const releaseMic = useCallback(async (): Promise<void> => {
    // Claimed before the await: the caller has to be able to hand the mic back from
    // its own teardown, and it can only know to do that if the flag is already set.
    micLentOut.current = true;
    if (micRetryTimer.current) clearTimeout(micRetryTimer.current);
    micRetryTimer.current = null;
    try {
      await deviceRef.current?.audio?.unsetInputDevice();
    } catch {
      // Nothing has been taken from the SDR yet, and the caller fails on its own if
      // the device is genuinely unavailable.
    }
  }, []);

  // Take the mic back. A rejection here is usually the other holder not having
  // finished letting go, so it escalates rather than failing on the first try — and
  // only the last rung touches the health record. The lend flag is held throughout:
  // dropping it early would re-arm the automatic paths against this one.
  const restoreMic = useCallback(async (): Promise<boolean> => {
    try {
      for (let attempt = 1; ; attempt++) {
        const audio = deviceRef.current?.audio;
        // No device to give it back to — before a voice token, or after a teardown.
        // Nothing was ever taken, so there is nothing to fail at, and reporting a
        // lost microphone here would be a fault the borrower did not cause.
        if (!audio) return true;
        // The remembered device only wins while it is still attached. A headset
        // unplugged during the loan leaves that id pointing at nothing, and the
        // automatic paths are stood down, so nothing else would ever move off it —
        // every rung would fail on a device that is gone while a working one sits
        // in the list.
        const remembered = selectedInputIdRef.current;
        const want =
          remembered && audio.availableInputDevices.has(remembered)
            ? remembered
            : firstInput(audio)?.deviceId;
        try {
          if (!want) throw new Error('No audio input devices');
          await audio.setInputDevice(want);
          stampMicAcquired();
          if (active.current) {
            setCurrentInputId(audio.inputDevice?.deviceId ?? want);
            refreshDevices();
          }
          return true;
        } catch (err: unknown) {
          const step = micRestoreStep(attempt);
          if (step === 'give_up') {
            // Its own stage: a give-up here is a different population from the
            // opportunistic re-acquire, and merging them hides which one is failing.
            reportAudioFailure('restore', err);
            stampMicFailed(err);
            return false;
          }
          await new Promise((r) => setTimeout(r, RETRY_AFTER_MS));
          // The prompt-raising half of a full acquire, inline rather than through the
          // SDR-facing path: that one stamps, schedules a retry of its own, and would
          // have to be let past the lend flag to run at all.
          if (step === 'recheck') {
            try {
              const probe = await navigator.mediaDevices.getUserMedia({ audio: true });
              probe.getTracks().forEach((t) => t.stop());
              await audio._updateAvailableDevices();
            } catch {
              // Nothing to do here — the next rung reports for both of us.
            }
          }
        }
      }
    } finally {
      micLentOut.current = false;
    }
  }, [refreshDevices, stampMicAcquired, stampMicFailed]);

  // Switch the speaker. Routes the live call's remote audio to the new output
  // immediately (where supported); remembered for next time.
  const selectOutputDevice = useCallback((deviceId: string): void => {
    const audio = deviceRef.current?.audio;
    if (!audio) return;
    writePref(LS_OUTPUT, deviceId);
    void audio.speakerDevices
      .set(deviceId)
      .then(() => {
        if (active.current) setCurrentOutputId(deviceId);
      })
      .catch((err: unknown) => {
        if (active.current)
          setLastError(`speaker: ${err instanceof Error ? err.message : 'unavailable'}`);
      });
  }, []);

  const teardown = useCallback(() => {
    active.current = false;
    setActiveEdge(null);
    setArmedEdge(undefined);
    setMicSilence(null);
    // Stop the poll before the peer connection dies, but do NOT clear the timing:
    // device.destroy() below chains synchronously to the call's `disconnect`, which runs
    // toReady and emits the row. Nulling here would blank every unmount-mid-call into a
    // `not_attached` cohort that never existed. `stopped` freezes the marks; it does not
    // erase them, and toReady clears the refs on its own path.
    detachSetup.current?.();
    const device = deviceRef.current;
    if (device) {
      try {
        void device.audio?.unsetInputDevice(); // release the mic (no live call at teardown)
      } catch {
        // ignore
      }
      try {
        device.destroy(); // unregister + tear down the connection
      } catch {
        // ignore
      }
      deviceRef.current = null;
    }
    if (gainRef.current) {
      try {
        gainRef.current.disconnect();
      } catch {
        // ignore
      }
      gainRef.current = null;
    }
    if (audioCtxRef.current) {
      void audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    audioElRef.current = null;
    wrappedEls.current = new WeakSet();
    inputWired.current = false;
    selectedInputIdRef.current = null;
    // A borrower that vanished mid-loan cannot hand the mic back, and a latched flag
    // would keep every automatic re-acquire standing down for the rest of the session.
    micLentOut.current = false;
    // Forget the dedupe so the next device re-reports its starting state rather
    // than being suppressed as "same as the session we just tore down".
    lastAudioKey.current = null;
    // A timer outliving this would deliver a verdict on a phone that no longer exists.
    if (micTimer.current) clearTimeout(micTimer.current);
    micTimer.current = null;
    if (micRetryTimer.current) clearTimeout(micRetryTimer.current);
    micRetryTimer.current = null;
    // Permission survives: it is a fact about the browser, not about the phone we
    // just tore down, and the Permissions effect only re-fires on an actual change —
    // so dropping it here would leave a denied SDR reading the cause-neutral copy.
    micHealth.current = { ...newMicHealth(), permission: micHealth.current.permission };
    setMicVerdict('pending');
    setMicRetried(false);
    inputLevelRef.current = 0;
    outputLevelRef.current = 0;
    callRef.current = null;
    awaitingRemoteRef.current = false;
    if (blipMuteTimer.current != null) {
      clearTimeout(blipMuteTimer.current);
      blipMuteTimer.current = null;
    }
    blipMuting.current = false;
    blipMutedAtRef.current = null;
    blipCallSidRef.current = null;
    legCallIdRef.current = null;
    clearVmGuard();
  }, [clearVmGuard]);

  // The rebuild itself: gate, log, tear down, re-arm, and let the token
  // effect's build path construct the replacement. `teardown()` flips `active`
  // off (it was written for disable/unmount), so re-arm it here — the hook is
  // still enabled and the next device's handlers must run.
  const requestRebuild = useCallback(
    (reason: RebuildReason, code: number | null = null, opts?: { freshToken?: boolean }) => {
      if (!active.current || rebuildCappedRef.current) return;
      if (callRef.current || !seatedRef.current) {
        // Deferred, not dropped: a live call outranks any rebuild, and a
        // seatless tab spends no budget (see the `seated` opt). The watchdog
        // tick consumes this at call end / seat join. Merged, not overwritten
        // — the pending must keep BOTH hard requirements across triggers
        // (non-cancellability, fresh-token wait); see mergePending.
        pendingRebuildRef.current = mergePending(pendingRebuildRef.current, reason);
        return;
      }
      const now = Date.now();
      const gate = decideRebuild(rebuildTimesRef.current, now);
      rebuildTimesRef.current = gate.history;
      if (gate.capped) {
        // Five failed revivals in an hour: a told human beats a silent zombie.
        rebuildCappedRef.current = true;
        setRebuildCapped(true);
        pendingRebuildRef.current = null;
        logCall(
          'device_rebuild_capped',
          { event: 'cockpit_device_rebuild_capped', reason },
          'warn',
        );
        return;
      }
      if (!gate.allow) {
        // Throttled, not dropped: the watchdog tick retries once the gap has
        // passed — and cancels it instead if the device is registered by then
        // and every merged requirement permits it.
        pendingRebuildRef.current = mergePending(pendingRebuildRef.current, reason);
        return;
      }
      pendingRebuildRef.current = null;
      watchdogRef.current = { breachAt: null, registerTriedAt: null };
      // The 31005 burst ledger describes ONE device generation: carrying two
      // strikes into the replacement would let a single transient 31005 on the
      // fresh phone complete the dead phone's burst and burn another slot.
      err31005TimesRef.current = [];
      const context = { reason, code, nth_this_hour: gate.history.length };
      rumAction('cockpit.device_rebuilt', context);
      logCall('device_rebuilt', { event: 'cockpit_device_rebuilt', ...context }, 'warn');
      teardown();
      active.current = true;
      setStatus('connecting');
      if ((opts?.freshToken ?? false) || reason === 'error_20101') {
        // The token we hold is the credential Twilio just REJECTED — building
        // with it would predictably fail the same way and burn another budget
        // slot. Wait for a changed token string instead: the token effect's
        // dep fires the build the moment a fresh mint lands (a fresh mint
        // always differs — its exp moves), and the watchdog re-nudges the
        // request if nothing arrives. `opts.freshToken` carries the same
        // requirement out of a merged deferral whose label is another reason.
        awaitingTokenRef.current = true;
      } else {
        setRebuildNonce((n) => n + 1);
      }
      // Ask for a fresh token: stale tokens are how wedges become 20101 loops.
      // With the WS down the frame is dropped — and covered: the BFF re-pushes
      // a token on every WS reconnect, and that change lands on the new device.
      onRefresh.current();
    },
    [teardown],
  );
  // Device/window handlers are wired once per build and must see the current
  // callback without re-subscribing (same pattern as onRefresh above).
  const requestRebuildRef = useRef(requestRebuild);
  requestRebuildRef.current = requestRebuild;

  // Watchdog for the silent wedge: no error fires — the Device just sits
  // unregistered while the page runs on. Poll the one truth the SDK keeps,
  // `device.state`, and escalate: one register() first (cheapest recovery when
  // the token is still valid), rebuild only if that didn't take. Also consumes
  // a rebuild deferred past a live call.
  const watchdogTickRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (!enabled) return;
    const tick = (): void => {
      if (!active.current) return;
      // Provably offline: a rebuilt Device cannot register by construction, so
      // escalating now would only burn the capped budget on a network problem
      // that isn't the phone's. The `online` wake handler gives the watchdog
      // an immediate look the moment connectivity returns.
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        watchdogRef.current = { breachAt: null, registerTriedAt: null };
        return;
      }
      const pending = pendingRebuildRef.current;
      if (seatedRef.current && !callRef.current && pending) {
        // A deferred state/transport rebuild is only honoured against a
        // still-unhealthy phone: for those reasons, a `registered` device IS
        // the recovery, and rebuilding it would create a brief dead seat. A
        // deferral carrying the ICE crash is consumed regardless —
        // registration only attests signalling, and that crash's failure mode
        // is a dead media layer under live signalling (the zombie shape).
        if (deviceRef.current?.state === 'registered' && pending.cancellable) {
          pendingRebuildRef.current = null;
        } else {
          // The merged requirements travel with the consumption: a deferral
          // that absorbed a 20101 keeps its fresh-token wait whatever its label.
          requestRebuildRef.current(pending.reason, null, { freshToken: pending.freshToken });
          return;
        }
      }
      const device = deviceRef.current;
      // A missing credential is the one wedge a rebuild cannot fix: whenever
      // there is no device to build (a teardown waiting on a token) or the
      // held token is null (the BFF pushed a failed mint), re-ask at a slow
      // cadence until a real token lands. Covers the 20101 fresh-token wait,
      // a refresh frame eaten by a nominally-open WS, and a token-null push
      // that a healthy phone survived. The WS-reconnect re-push covers the
      // common case; this covers the rest.
      if (!device || tokenRef.current === null) {
        // Exponential backoff, not elapsed-time alone: under a persistent
        // token:null (unmapped SDR, sustained mint outage, the local
        // simulated-device path) a fleet of tabs asking every 30s forever
        // would hammer the BFF→core mint path, which has no coalescing. The
        // delay doubles to a 10-minute ceiling and resets when a token lands.
        const now = Date.now();
        if (now >= tokenAskRef.current.nextAt) {
          tokenAskRef.current = {
            nextAt: now + tokenAskRef.current.delayMs,
            delayMs: Math.min(tokenAskRef.current.delayMs * 2, TOKEN_REASK_MAX_MS),
          };
          onRefresh.current();
        }
      }
      if (!device || callRef.current) {
        watchdogRef.current = { breachAt: null, registerTriedAt: null };
        return;
      }
      // Seatless tabs get the register() arm below (free healing — a fixed
      // phone before the SDR joins is a wedge that never happens) but any
      // `rebuild` verdict is pended by requestRebuild until they hold a seat.
      const step = watchdogStep(device.state, watchdogRef.current, Date.now());
      watchdogRef.current = step.next;
      if (step.action === 'register') {
        logCall('device_register_retry', {
          event: 'cockpit_device_register_retry',
          device_state: device.state,
        });
        void device.register().catch(() => {
          // The rebuild arm of the watchdog handles a register that won't take.
        });
      } else if (step.action === 'rebuild') {
        requestRebuildRef.current('watchdog');
      }
    };
    watchdogTickRef.current = tick;
    const id = setInterval(tick, WATCHDOG_TICK_MS);
    return () => {
      clearInterval(id);
      watchdogTickRef.current = null;
    };
  }, [enabled]);

  // The SDK's ICE-restart crash (twilio-voice.js#99, unfixed upstream since
  // 2022) is unhandled — without this listener the app cannot know its Device
  // just died. Passive: no preventDefault, nothing swallowed; it only
  // schedules the rebuild.
  useEffect(() => {
    if (!enabled) return;
    const onRejection = (ev: PromiseRejectionEvent): void => {
      const r = ev.reason as { message?: unknown; stack?: unknown } | null | undefined;
      if (matchesIceRestartCrash(r?.message, r?.stack)) requestRebuildRef.current('ice_crash');
    };
    const onError = (ev: ErrorEvent): void => {
      const stack = ev.error instanceof Error ? ev.error.stack : undefined;
      if (matchesIceRestartCrash(ev.message, stack)) requestRebuildRef.current('ice_crash');
    };
    window.addEventListener('unhandledrejection', onRejection);
    window.addEventListener('error', onError);
    return () => {
      window.removeEventListener('unhandledrejection', onRejection);
      window.removeEventListener('error', onError);
    };
  }, [enabled]);

  // Wake refresh: sleep and network loss kill the BFF WS and the Twilio WS
  // together, so a `tokenWillExpire` that fired while the link was down asked
  // nobody, and the Device would reconnect with a stale token (→ 20101). Wake
  // is the moment that matters — request a fresh token then, and give the
  // watchdog an immediate look instead of waiting out its interval.
  useEffect(() => {
    if (!enabled) return;
    const wake = (): void => {
      if (!active.current) return;
      const now = Date.now();
      if (now - lastWakeRefreshAtRef.current < WAKE_REFRESH_MIN_GAP_MS) return;
      lastWakeRefreshAtRef.current = now;
      onRefresh.current();
      watchdogTickRef.current?.();
    };
    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') wake();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', wake);
    window.addEventListener('online', wake);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', wake);
      window.removeEventListener('online', wake);
    };
  }, [enabled]);

  // Lifecycle: device is alive only while enabled; teardown on disable/unmount.
  useEffect(() => {
    if (!enabled) {
      teardown();
      setStatus('idle');
      setInputDevices([]);
      setOutputDevices([]);
      setCurrentInputId(null);
      setCurrentOutputId(null);
      setDeviceAlert(null);
      // A disabled phone owes nothing: forget deferred rebuilds and re-arm the
      // cap banner for the next session (the rebuild-history window still
      // applies, so re-enabling is not a way around the hourly cap).
      pendingRebuildRef.current = null;
      rebuildCappedRef.current = false;
      setRebuildCapped(false);
      return;
    }
    active.current = true;
    return () => teardown();
  }, [enabled, teardown]);

  // Token: construct on first real token, refresh in place on change. With no
  // token there is no device — status stays `idle` and the controls disable.
  const token = voiceToken?.token ?? null;
  // Mirrored for the watchdog tick (a timer context): a null here is what
  // tells it to keep re-asking the BFF for a real credential.
  const tokenRef = useRef(token);
  tokenRef.current = token;
  useEffect(() => {
    if (!enabled) return;

    if (token) {
      // A real token landed: the re-ask backoff starts over from its base.
      tokenAskRef.current = { nextAt: 0, delayMs: TOKEN_REASK_BASE_MS };
      const existing = deviceRef.current;
      if (existing) {
        try {
          existing.updateToken(token); // refresh, don't rebuild
        } catch {
          // ignore
        }
        return;
      }
      setStatus('connecting');
      const resolved = resolvePref();
      if (!edgeReported) {
        edgeReported = true;
        // Both paths matter. A typo left unreported would have the SDR work a normal
        // shift while the data reads as an untreated control; a SUCCESSFUL arm left
        // unreported is indistinguishable from no arm until a call completes, so an
        // SDR who never connects one looks like a control too.
        if (URL_ARM.kind === 'invalid') {
          logCall(
            'edge_rejected',
            { event: 'cockpit_edge_rejected', requested_edge: URL_ARM.raw, active_edge: null },
            'warn',
          );
        } else if (resolved.edge) {
          logCall('edge_armed', {
            event: 'cockpit_edge_armed',
            requested_edge: resolved.edge,
            // Neither a self-selected arm nor a geographic default is a controlled
            // assignment, so an analysis has to be able to exclude both rather than
            // assume nobody touched the picker.
            arm_source: resolved.source,
          });
        } else if (GEO_EDGE) {
          // Otherwise an SDR who turned the default off is indistinguishable from the
          // rule silently failing to fire.
          logCall('edge_opted_out', { event: 'cockpit_edge_opted_out', geo_edge: GEO_EDGE });
        }
      }
      const device = new Device(token, {
        codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU], // Opus preferred
        logLevel: 'error',
        // Empty unless an edge resolved, so a session with neither an arm nor a
        // regional default gets an options object identical to the one that shipped
        // before this existed. The chain and its signalling timeout are inseparable —
        // see deviceEdgeOptions.
        ...deviceEdgeOptions(resolved.edge),
      });
      armedEdgeRef.current = resolved.edge;
      armedSourceRef.current = resolved.source;
      setArmedEdge(resolved.edge);
      deviceRef.current = device;
      // Re-arm unconditionally: any teardown (rebuild, token-null push, a
      // prior disable) left `active` false, and this branch is only reachable
      // while enabled. Without this, a device built after a token-null push
      // would register and take calls with every active-gated handler dead —
      // a half-alive phone, the exact failure class this hook exists to kill.
      active.current = true;
      // Whatever token string reached this build is by definition fresh enough
      // to try; the 20101 wait (see requestRebuild) is over.
      awaitingTokenRef.current = false;

      // Reset per-call telemetry back to the idle baseline.
      const resetCallTelemetry = (): void => {
        outputLevelRef.current = 0;
        if (active.current) {
          setQuality('unknown');
          setWarnings([]);
          setDeviceRtt(null);
        }
      };

      const wireCall = (call: Call): void => {
        callRef.current = call;
        // The CallSid, for the WHOLE call and independently of the blip mute. Kept for
        // correlating with Twilio's own logs — but it is the CHILD client leg's sid, NOT
        // the parent the core stores, so it does not join to `call_events.call_sid`.
        blipCallSidRef.current = call.parameters?.CallSid ?? null;
        // The core's call id, carried on the leg as a custom parameter. Available here,
        // before any card, which is the whole point — see `legCallIdRef`.
        legCallIdRef.current = call.customParameters?.get('call_id') ?? null;
        // Per-call audio telemetry. Locals, not refs: a superseded call's late events must
        // not reach the live call's ledgers, and its row is gated on its own accept.
        let callAcceptedAt: number | null = null;
        const micLevel = newMicLevel();
        const media = newMediaStats();
        const micTrack = newMicTrack();
        const inputWarning = newInputWarning();
        let stopMedia: (() => void) | null = null;
        const sinceAccept = (): number | null =>
          callAcceptedAt == null ? null : Math.round(performance.now() - callAcceptedAt);
        // The SDK swaps the capture track in place on an input change, without an event.
        const liveTrack = () => call.getLocalStream()?.getAudioTracks()[0] ?? null;
        const recheckTrack = (): void => {
          trackSeen(micTrack, liveTrack());
        };
        const silentMic = newSilentMic();
        // The first raise, kept after a clear: the row describes the call, not its end.
        let silentRaised: { source: MicSilenceSource; at: number } | null = null;
        let quietObserved = false;
        const applyMic = (t: MicTransition | null): void => {
          if (!t) return;
          const now = performance.now();
          const inputDevice = deviceRef.current?.audio?.inputDevice?.label || null;
          if (t.type === 'raised') {
            silentRaised ??= { source: t.source, at: now };
            if (active.current && callRef.current === call) setMicSilence({ source: t.source });
            const context = {
              ...audioIds(),
              kind: 'silent',
              source: t.source,
              // Raised during the pre-card window, the banner waits for the card.
              card_shown: cardShownRef.current,
              window_max: t.windowMax,
              ms_since_accept: sinceAccept(),
              ms_since_remote: t.msSinceRemote,
              input_device: inputDevice,
              visibility_state: tabVisibility(),
            };
            rumAction('cockpit.mic.silent', context);
            logCall('mic_silent', { event: 'cockpit_mic_silent', ...context }, 'warn');
          } else if (t.type === 'observed_quiet') {
            quietObserved = true;
            logCall('mic_silent', {
              event: 'cockpit_mic_silent',
              ...audioIds(),
              kind: 'quiet',
              window_max: t.windowMax,
              ms_since_accept: sinceAccept(),
              ms_since_remote: t.msSinceRemote,
              input_device: inputDevice,
              visibility_state: tabVisibility(),
            });
          } else {
            if (active.current && callRef.current === call) setMicSilence(null);
            logCall('mic_silent_cleared', {
              event: 'cockpit_mic_silent_cleared',
              ...audioIds(),
              reason: t.reason,
              source: t.source,
              ms_since_raise: t.msSinceRaise,
            });
          }
        };
        // Built inside toReady, so it must never throw: an exception here would skip every
        // reset that follows and strand the next call.
        const callAudioTelemetry = (): Record<string, unknown> => {
          try {
            recheckTrack();
            trackEnd(micTrack);
            const audio = deviceRef.current?.audio;
            const snapshot = buildAudioSnapshot(
              audio ? [...audio.availableInputDevices.values()] : [],
              selectedInputIdRef.current ?? audio?.inputDevice?.deviceId ?? null,
            );
            return {
              ...reportLevel(micLevel),
              ...media.report,
              ...reportTrack(micTrack, snapshot.selectedLabel, snapshot.resolvedLabel),
              ...reportInputWarning(inputWarning, callAcceptedAt, performance.now()),
              mic_silent_raised: silentRaised != null,
              mic_silent_ms:
                silentRaised == null || callAcceptedAt == null
                  ? null
                  : Math.round(silentRaised.at - callAcceptedAt),
              mic_silent_source: silentRaised?.source ?? null,
              mic_quiet_observed: quietObserved,
            };
          } catch (err) {
            return { telemetry_error: err instanceof Error ? err.message : String(err) };
          }
        };
        const toReady = (): void => {
          const wasLive = callRef.current === call;
          if (wasLive) callRef.current = null;
          // BEFORE clearVmGuard, which cancels the timer and wipes the two observations
          // this reads. An accepted call that ends inside the window would otherwise
          // never emit an opening row — and those are the short, dead-air, hung-up-early
          // calls, i.e. exactly the numerator. Dropping them from the denominator would
          // have flattered every voicemail and dead-air rate computed from it. The latch
          // inside makes this a no-op when the window already reported.
          if (liveness.current.startedAt != null) reportOpening();
          clearVmGuard();
          // A gate still raised at teardown: the call ended some other way while the
          // prompt was up (the SDR disposed it, or the far side hung up). Emit the
          // stayed receipt — delivery is best-effort (useCockpit drops it if the
          // phase already moved), and a dropped one is derivable from the call's own
          // manual_dropped / sdr_hangup rows landing after the raised receipt.
          if (isRaised(silenceGateState.current)) dismissSilenceGate('disposed');
          clearSilenceCutTimer();
          if (active.current) setSilencePrompt(null);
          // Restore the speaker if the call ended mid-blip-window (disconnect,
          // cancel, reject, error) — otherwise the NEXT call inherits a muted
          // element / zeroed gain and the SDR sits in silence.
          unmuteRemoteAudio('call_ended');
          // The call's audio verdict, emitted BEFORE the ids are cleared below. One row
          // per call, carrying both the numerator (one_way / silent_both_ways) and its
          // own denominator, so a rate is answerable without joining anything.
          //
          // ONLY for a call this browser accepted. `toReady` also runs for cancel /
          // reject / error, and a leg the lead abandoned while the browser was still
          // ringing has no audio to report — it would land as `silent_both_ways: true`
          // and, under predictive dialling, those legs would swamp the very population
          // this row exists to size. `startedAt` is stamped at accept and nowhere else.
          if (liveness.current.startedAt != null) {
            const heard = reportLiveness(liveness.current);
            // Captured ONCE. netReport() re-reads the live setup timing, which is
            // nulled a few lines below, so a second call here would report nothing
            // — and the handshake bytes must come from the same object that is
            // logged, or the SDR's displayed verdict could disagree with the row
            // we analyse it from.
            const report = netReport();
            const bytes = report.setup_handshake_avg_bytes_sent;
            if (typeof bytes === 'number' && Number.isFinite(bytes)) {
              const next = [...pqSamples.current, bytes].slice(-MAX_SAMPLES);
              pqSamples.current = next;
              if (active.current) setPqVerdict((prev) => settleVerdict(prev, rawVerdict(next)));
            }
            logCall(
              'call_audio',
              {
                event: 'cockpit_call_audio',
                ...audioIds(),
                ...heard,
                ...report,
                // A superseded call's row carries the live call's ids and liveness, so its
                // own telemetry would be mislabelled there.
                ...(wasLive && callAcceptedAt != null ? callAudioTelemetry() : {}),
              },
              heard.one_way ? 'warn' : 'info',
            );
          }
          liveness.current = newAudioLiveness();
          // Unconditional, and OUTSIDE the `startedAt != null` branch above: toReady also
          // runs for cancel / reject / error, and a probe left attached on those paths
          // would poll a dead peer connection until its own 20s cap. netReport() has
          // already read the timing, just above, while it was still live.
          detachSetup.current?.();
          detachSetup.current = null;
          stopMedia?.();
          stopMedia = null;
          setupTiming.current = null;
          acceptInvokedAt.current = null;
          resetNet();
          // Clear the blip window with the call. A card that lands without an
          // accept-time mute (it raced accept) must report `blip_ms: null`, not the
          // elapsed time since the PREVIOUS call's window opened.
          blipMutedAtRef.current = null;
          blipCallSidRef.current = null;
          legCallIdRef.current = null;
          resetCallTelemetry();
          awaitingRemoteRef.current = false;
          // Only when this was the live call: a late teardown event from a superseded
          // call must not clear the mic-silence state of the call now in progress.
          if (callRef.current == null && active.current) {
            setMicSilence(null);
          }
          if (active.current) {
            setAwaitingRemote(false);
            setHumanUnconfirmed(false);
            setStatus('ready');
          }
        };
        call.on('accept', () => {
          if (active.current) setStatus('on_call');
          // The ONLY client-confirmed record that the SDR's browser actually joined this
          // call. The core's `bridged`/`bridged_at` is stamped before the TwiML naming
          // this client is even returned (twilio_controller.ex `send_bridged`), so it
          // records our intent to dial the browser, never the browser answering.
          //
          // `blip` states WHY this call did or did not get a blip window, so the
          // no-mute cases are labelled rather than inferred from a missing log:
          //   'muted'     the speaker was shut; expect a matching cockpit_blip_unmuted,
          //               and its ABSENCE is a real failure-to-unmute (tab closed mid-call)
          //   'card_race' the card beat the accept, so nothing was ever muted and the SDR
          //               heard the lead immediately — healthy, and the only no-mute case
          //               that says anything about latency
          //   'disabled'  the feature is off; no window exists to reason about
          const blip = !BLIP_MUTE_ENABLED
            ? 'disabled'
            : cardShownRef.current
              ? 'card_race'
              : 'muted';
          logCall('accepted', { event: 'cockpit_call_accepted', ...audioIds(), blip });
          // Stamped from accept, so `first_remote_audible_ms` measures the thing the SDR
          // experiences: how long after joining before they could hear the lead at all.
          // Hoisted so the setup probe below shares the exact same origin — the two are
          // only subtractable if they are stamped off one clock read.
          const acceptedAt = performance.now();
          liveness.current = newAudioLiveness(acceptedAt);
          callAcceptedAt = acceptedAt;
          // Telemetry only: nothing here may break accepting the call.
          try {
            trackAccepted(micTrack, liveTrack());
            stopMedia = attachMediaStatsPoller({
              call,
              stats: media,
              sinceAccept,
              onPoll: recheckTrack,
            });
          } catch {
            // The row reports nulls for whatever did not attach.
          }
          // Per call, not per device: a superseded call's teardown leaves its banner
          // standing, and back-to-back calls would carry it onto an audible one.
          if (active.current) setMicSilence(null);
          // Detach any previous probe FIRST: back-to-back calls would otherwise leave the
          // old poll running against a dead peer connection.
          detachSetup.current?.();
          const timing = newSetupTiming(acceptedAt, acceptInvokedAt.current);
          setupTiming.current = timing;
          detachSetup.current = attachSetupProbe(call, timing);
          resetNet();
          openingReported.current = false;
          // Fresh call: connected, but the far side hasn't spoken yet → the cockpit
          // shows "Connecting to …" until the volume handler hears them (below).
          awaitingRemoteRef.current = true;
          if (active.current) setAwaitingRemote(true);
          // Skip if the card already arrived, racing ahead of accept. The `audio`
          // handler re-asserts the mute when the playback element shows up, since that
          // fires after accept.
          if (BLIP_MUTE_ENABLED && !cardShownRef.current) {
            muteRemoteAudio();
            blipMuteTimer.current = setTimeout(() => {
              blipMuteTimer.current = null;
              if (callRef.current !== call) return; // call already gone
              // No card in BLIP_MUTE_MAX_MS: we are about to open the SDR's speaker
              // with nothing on screen telling them who is on the line.
              unmuteRemoteAudio('cap');
            }, BLIP_MUTE_MAX_MS);
          }
          // Arm the silent-voicemail guard for THIS call (cockpit/voicemail.ts): a
          // one-shot timer; the volume handler below feeds its two conditions and
          // cancels it on any far-side audio. At expiry, isVoicemail decides whether
          // to raise the banner.
          clearVmGuard();
          if (active.current) setHumanUnconfirmed(false); // clear any stale prompt
          vmTimer.current = setTimeout(() => {
            vmTimer.current = null;
            if (callRef.current !== call) return; // call already gone
            // Reported before the verdict gate, and also from the volume handler when
            // the far side is heard first — that path cancels this timer, so reporting
            // only here would have recorded nothing but the unhealthy tail.
            reportOpening();
            const hit = isVoicemail({
              remoteHeard: vmRemoteHeard.current,
              localSpoke: vmLocalSpoke.current,
              // From the ref: this fires up to windowMs after the handler closed over
              // the prop, which is the whole window a verdict has to land in.
              humanConfirmed: humanConfirmedRef.current,
            });
            if (!hit) return;
            if (active.current) setHumanUnconfirmed(true);
            onPromptRaised.current?.();
          }, voicemailConfig.windowMs);
        });
        call.on('disconnect', toReady);
        call.on('cancel', toReady);
        call.on('reject', toReady);
        call.on('error', (err: TwilioErrorLike) => {
          if (active.current) setLastError(err?.message ?? 'call error');
          onClientErrorRef.current?.(typeof err?.code === 'number' ? err.code : null);
          toReady();
        });
        // Remote-speaking wave: the 2nd arg is the remote/output level (0–1). Also
        // feeds the silent-voicemail guard while its timer is armed: ANY far-side
        // audio above remoteMax cancels it (a human, or a real voicemail greeting);
        // our own speech above localMin arms the "we spoke" condition.
        call.on('volume', (input: number, output: number) => {
          outputLevelRef.current = output;
          // FIRST, and deliberately before any threshold: the event firing at all is the
          // measurement. Its value is irrelevant — silence still means the remote track
          // was ATTACHED, which is all this marks. It is NOT evidence the media path is
          // up: the analyser starts from `ontrack`, before ICE and DTLS. Gating this on
          // audibility would collapse it onto `first_remote_audible_ms` and measure
          // nothing new.
          {
            const startedAt = liveness.current.startedAt;
            if (net.current.firstVolumeMs == null && startedAt != null) {
              net.current.firstVolumeMs = Math.max(0, Math.round(performance.now() - startedAt));
            }
          }
          // Whole-call two-sided facts. Kept outside the vm* branch below, which only
          // runs while the guard's 10s window is armed — "was there ever a conversation"
          // is a question about the call, not about the opening.
          if (callAcceptedAt != null) {
            const now = performance.now();
            observeLevel(micLevel, input, now);
            if (callRef.current === call) {
              applyMic(
                observeMic(silentMic, {
                  input,
                  remoteAudible: remoteAudible(output, voicemailConfig),
                  now,
                }),
              );
            }
          }
          const heardSdr = localAudible(input, voicemailConfig);
          if (heardSdr) onLocalAudibleRef.current?.();
          observeLiveness(liveness.current, {
            remoteAudible: remoteAudible(output, voicemailConfig),
            localAudible: heardSdr,
            now: performance.now(),
          });
          // UI: the far side has spoken — drop the "Connecting to …" label. One-shot
          // per call, and decoupled from the voicemail timer so a call where we never
          // spoke (no auto-hangup) still flips the moment they talk, even post-window.
          if (awaitingRemoteRef.current && remoteAudible(output, voicemailConfig)) {
            awaitingRemoteRef.current = false;
            if (active.current) setAwaitingRemote(false);
          }
          if (vmTimer.current != null) {
            if (remoteAudible(output, voicemailConfig)) {
              vmRemoteHeard.current = true;
              clearTimeout(vmTimer.current);
              vmTimer.current = null;
              // The window ends HERE on a healthy call, so this is the only chance to
              // record it. The timer body below never runs for these.
              reportOpening();
            } else if (localAudible(input, voicemailConfig)) {
              vmLocalSpoke.current = true;
            }
          } else if (
            active.current &&
            humanUnconfirmedRef.current &&
            remoteAudible(output, voicemailConfig)
          ) {
            // The prompt is up (timer already fired) but the far side has now spoken —
            // it was a live human after all, so auto-dismiss the banner.
            setHumanUnconfirmed(false);
          }
          // The mutual-silence gate (cockpit/silence-gate.ts), fed the SAME audibility
          // predicates as the liveness facts above — but against the CARD's live
          // thresholds, so a console retune needs no deploy. Runs only while a card
          // is up: card time is the SDR cost being bought back.
          {
            const cfg = silenceCfgRef.current;
            const mode = gateMode(cfg);
            if (cfg != null && mode !== 'off' && cardShownRef.current) {
              const gs = silenceGateState.current;
              const now = performance.now();
              const gateRemote = remoteAudible(output, cfg);
              const gateLocal = localAudible(input, cfg);
              observeSilence(gs, { remoteAudible: gateRemote, localAudible: gateLocal, now });
              const firing = isRaised(gs) ? null : shouldFire(gs, cfg, now);
              if (isRaised(gs)) {
                // The call proved live — stand down. Which sounds count depends on
                // the tier: a mutual fire is dismissed by either side, but a
                // lead-only fire only by the LEAD — the SDR's own noise is exactly
                // what that tier exists to see through.
                const dismissedBySound = gateRemote || (gateLocal && gs.firedRule !== 'lead_only');
                if (dismissedBySound) dismissSilenceGate('sound');
              } else if (firing) {
                markFired(gs, now, firing);
                onSilenceGateRef.current?.({
                  action: 'raised',
                  mode,
                  rule: firing,
                  cardAgeMs: Math.round(cardAge(gs, now)),
                  quietMs: Math.round(gs.quietMsAtFire ?? 0),
                  visibilityState: tabVisibility(),
                });
                // ON is the full behaviour (plan ground rules): banner, countdown,
                // cut on expiry. There is no promptless or bluffing state.
                if (active.current) {
                  setSilencePrompt({ deadlineAt: Date.now() + cfg.graceMs, rule: firing });
                }
                silenceCutTimer.current = setTimeout(() => {
                  silenceCutTimer.current = null;
                  if (callRef.current !== call) return; // call already gone
                  const cutState = silenceGateState.current;
                  if (!isRaised(cutState)) return; // dismissed in the meantime
                  markDismissed(cutState, performance.now());
                  if (active.current) setSilencePrompt(null);
                  // The caller ends the call (endCall('gate')) — the hook only
                  // reports. ended_by stays sdr_hangup; THIS receipt is what
                  // separates a gate cut from an SDR-chosen hangup.
                  onSilenceGateRef.current?.({
                    action: 'cut',
                    mode,
                    ...(cutState.firedRule ? { rule: cutState.firedRule } : {}),
                    countdownMs: cfg.graceMs,
                    // Sampled again, not carried from the raise: a tab nobody ever
                    // looked at and one walked away from mid-countdown are different
                    // failures, and one reading cannot tell them apart.
                    visibilityState: tabVisibility(),
                  });
                }, cfg.graceMs);
              }
            }
          }
        });
        // Per-second RTC stats → quality + device RTT (already ~1Hz).
        call.on('sample', (sample: SampleLike) => {
          // Folded BEFORE the active guard: the summary is per call, and a sample that
          // lands as the hook is tearing down is still evidence about that call.
          const n = net.current;
          n.samples += 1;
          if (typeof sample.rtt === 'number') {
            n.rttSum += sample.rtt;
            n.rttMax = Math.max(n.rttMax, sample.rtt);
          }
          if (typeof sample.mos === 'number') {
            n.mosMin = n.mosMin == null ? sample.mos : Math.min(n.mosMin, sample.mos);
          }
          if (typeof sample.packetsLostFraction === 'number') {
            n.lossMax = Math.max(n.lossMax, sample.packetsLostFraction);
          }
          if (typeof sample.codecName === 'string') n.codec = sample.codecName;
          // Measured from accept, the same origin as `first_remote_audible_ms`, so the
          // two are directly subtractable.
          const startedAt = liveness.current.startedAt;
          if (
            n.firstMediaMs == null &&
            startedAt != null &&
            typeof sample.bytesReceived === 'number' &&
            sample.bytesReceived > 0
          ) {
            n.firstMediaMs = Math.max(0, Math.round(performance.now() - startedAt));
          }
          if (!active.current) return;
          if (typeof sample.rtt === 'number') setDeviceRtt(Math.round(sample.rtt));
          setQuality(deriveCallQuality(sample));
        });
        call.on('warning', (name: string) => {
          // Accumulated for the call, never cleared: `warning-cleared` below un-sets the
          // UI badge, but "this call had high-rtt at some point" stays true afterwards.
          warningsRef.current.add(name);
          if (name === INPUT_LEVEL_WARNING) {
            inputWarningRaised(inputWarning, performance.now());
            if (callAcceptedAt != null && callRef.current === call) {
              applyMic(raiseFromSdk(silentMic, performance.now()));
            }
          }
          if (active.current) setWarnings((w) => (w.includes(name) ? w : [...w, name]));
        });
        call.on('warning-cleared', (name: string) => {
          if (name === INPUT_LEVEL_WARNING) {
            inputWarningCleared(inputWarning, performance.now());
            applyMic(clearFromSdk(silentMic, performance.now()));
          }
          if (active.current) setWarnings((w) => w.filter((x) => x !== name));
        });
        // The call's playback element — remember it and (if the user has already
        // engaged volume control) route it through the gain node.
        call.on('audio', (el: HTMLAudioElement) => {
          audioElRef.current = el;
          applyGainTo(el);
          // The element arrives AFTER accept, so re-assert an in-progress blip
          // mute onto it (and the freshly-wrapped gain node), or the first
          // greeting frames would leak before the window's timer fires.
          if (blipMuting.current) muteRemoteAudio();
        });
      };

      // A device was plugged/unplugged/relabelled. Refresh the lists, then try to
      // keep the SDR on their remembered devices — restoring the mic if it (re)
      // appeared, or warning if the active mic vanished and Twilio fell back to a
      // possibly-dead "default". Output is best-effort restored, no warning.
      const handleDeviceChange = (): void => {
        const audio = device.audio;
        if (!audio || !active.current) return;
        // Our own release, the recorder opening the device, and a mid-recording
        // unplug all fire this. Restoring here would race whoever holds the mic.
        if (micLentOut.current) return;
        refreshDevices();
        // Report the new hardware picture. Reported here (synchronously) AND again
        // after any restore below resolves, because a restore re-points the mic: the
        // dedupe collapses the pair when the restore was a no-op, and emits both when
        // it actually moved the SDR.
        const reportNow = (reason: AudioReportReason = 'device_change'): void =>
          reportAudioSnapshot(
            reason,
            [...audio.availableInputDevices.values()],
            audio.inputDevice?.deviceId ?? null,
            lastAudioKey,
          );
        reportNow();
        // How the banner clears without the SDR pressing anything. The restore below
        // cannot do it: it needs a remembered id present in the list, and a failed
        // first acquire leaves neither.
        const verdict = micState(micHealth.current, Date.now());
        if (verdict === 'warn' || verdict === 'blocked') {
          if (Date.now() - lastAcquireAt.current >= RETRY_AFTER_MS) acquireMic('reacquire');
          return;
        }
        const wantIn = readPref(LS_INPUT) ?? selectedInputIdRef.current;
        if (wantIn && audio.availableInputDevices.has(wantIn)) {
          if (audio.inputDevice?.deviceId !== wantIn) {
            void audio
              .setInputDevice(wantIn)
              .then(() => {
                stampMicAcquired();
                // The loan can start while this is in flight; the borrower's own
                // restore is what puts the device back, not this.
                if (!active.current || micLentOut.current) return;
                setCurrentInputId(audio.inputDevice?.deviceId ?? wantIn);
                setDeviceAlert(null);
                reportNow();
              })
              // Listed but will not open — held by another app, or permission went
              // away between the two calls. Silent, but not a healthy mic.
              .catch((err: unknown) => stampMicFailed(err));
          } else {
            setDeviceAlert(null);
          }
        } else if (selectedInputIdRef.current) {
          setDeviceAlert(
            `Microphone disconnected — now using "${audio.inputDevice?.label || 'system default'}". Pick your device below.`,
          );
        }
        const wantOut = readPref(LS_OUTPUT);
        if (
          audio.isOutputSelectionSupported &&
          wantOut &&
          audio.availableOutputDevices.has(wantOut)
        ) {
          const cur = [...audio.speakerDevices.get()][0]?.deviceId;
          if (cur !== wantOut) {
            try {
              void audio.speakerDevices.set(wantOut);
            } catch {
              // ignore
            }
          }
        }
      };

      const wireAudioMetering = (): void => {
        if (inputWired.current) return;
        const audio = device.audio;
        if (!audio) return;
        inputWired.current = true;
        setVolumeSupported(audio.isVolumeSupported);
        setOutputSelectionSupported(audio.isOutputSelectionSupported);
        acquireMic();
        // Feed the mic wave from Twilio's inputVolume (only if the browser can
        // analyse volume — device selection still works without it).
        if (audio.isVolumeSupported) {
          audio.on('inputVolume', (v: number) => {
            inputLevelRef.current = v;
          });
        }
        audio.on('deviceChange', handleDeviceChange);
      };

      device.on('registered', () => {
        if (!active.current) return;
        setStatus('ready');
        // Only meaningful once registered: `edge` is assigned from the signalling
        // handshake, so before this it is null even on a pinned Device.
        setActiveEdge(device.edge ?? null);
        wireAudioMetering();
      });
      device.on('unregistered', () => {
        if (!active.current) return;
        setStatus('connecting');
        // Stale here is worse than absent: the panel presents this as the connection
        // in use, and there is no connection.
        setActiveEdge(null);
      });
      // Device-scoped errors are NOT evidence a call ended. Twilio passes the `Call` only
      // when the fault belongs to one, and the commonest device error by far is 31205
      // (token expired) — which this hook expects and recovers from via `tokenWillExpire`
      // below. Reporting it as an ending would be unrecoverable: the CallServer latches
      // `ended_by_client_error?` and that latch outranks both the SDR and the contact, so a
      // one-second token blip would attribute a call the SDR then works for ten minutes to a
      // browser crash. Token expiry is fleet-wide, so it would mis-attribute in bursts.
      // Genuine mid-call faults arrive on the call-scoped `call.on('error')` above.
      device.on('error', (err: TwilioErrorLike, errCall?: Call) => {
        if (!active.current) return;
        setLastError(err?.message ?? 'device error');
        setStatus('error');
        if (errCall) onClientErrorRef.current?.(typeof err?.code === 'number' ? err.code : null);
        // Self-healing: terminal codes rebuild the Device. 31205 deliberately
        // does not (tokenWillExpire recovers it, and token expiry is
        // fleet-wide — rebuilding on it would synchronize a rebuild storm);
        // a lone 31005 does not either — only a burst marks a wedge. The
        // attribution above is untouched: a device error still never ends a
        // call.
        const code = typeof err?.code === 'number' ? err.code : null;
        let reason = terminalErrorReason(code);
        if (code === 31005) {
          const burst = record31005(err31005TimesRef.current, Date.now());
          err31005TimesRef.current = burst.times;
          if (burst.tripped) reason = 'error_31005_repeated';
        }
        if (reason) requestRebuildRef.current(reason, code);
      });
      device.on('incoming', (call: Call) => {
        // Twilio rings every endpoint registered for this identity, so a tab the SDR
        // left seatless competes for a call it can never show (inbound-guard.ts).
        const decision = decideInboundRef.current();
        const context = {
          answer: decision.answer,
          reason: decision.reason,
          phase: decision.phase,
          unseated_ms: decision.unseatedMs,
          // The CHILD CallSid. Twilio's docs do not say whether sibling endpoints of
          // one identity share it, so do NOT build a query that joins an SDR's tabs on
          // this — and note both tabs share one RUM session (it is cookie-scoped per
          // origin), so any per-tab reading has to group by view id.
          call_sid: call.parameters?.CallSid ?? null,
        };
        // Both outcomes, once per ring. The action is the session-replay marker; the
        // log is what a monitor can actually query — an `answer` rate falling to zero
        // for an SDR is the ring-out regression this could cause.
        rumAction('cockpit.call.inbound_decision', context);
        logCall('inbound_decision', { event: 'cockpit_inbound_decision', ...context });
        if (!decision.answer) {
          // `ignore`, never `reject`: ignore is local-only — Twilio's docs are explicit
          // that it sends no hangup and "the dialing party will continue to hear
          // ringing until another Device instance with the same identity accepts", so
          // the SDR's seated tab keeps ringing. `reject` DOES signal a hangup, and its
          // effect on the parent dial is undocumented.
          //
          // One-way: an ignored call can no longer be accepted, so a tab that declines
          // cannot take this ring even if the SDR joins a pool a second later.
          call.ignore();
          return;
        }
        wireCall(call);
        acceptInvokedAt.current = performance.now();
        call.accept(); // predictive dial — connect the waiting agent immediately
      });
      device.on('tokenWillExpire', () => onRefresh.current());

      void device.register().catch((err: unknown) => {
        // The identity check keeps a destroyed generation's late rejection from
        // writing into the session of the device that replaced it (`active` is
        // one boolean across generations; a rebuild re-arms it immediately).
        if (active.current && deviceRef.current === device)
          setLastError(err instanceof Error ? err.message : 'register failed');
      });
      return;
    }

    // No token. A null push usually means the BFF's core mint FAILED
    // (bff/src/cockpit/voice.ts pushes token:null on failure) — it is NOT
    // evidence this device's credential stopped working: a registered phone
    // keeps its token until exp. Destroying a healthy phone (possibly
    // mid-call) over a transient mint failure would turn a harmless token
    // refresh into an outage, so a device that is registered or on a call
    // SURVIVES the null push, and the watchdog re-asks for a token until a
    // real one lands. Only a device that is neither is torn down — that one
    // really is a dead phone not worth keeping.
    const survivor = deviceRef.current;
    if (survivor && (callRef.current || survivor.state === 'registered')) {
      active.current = true;
      return;
    }
    if (survivor) teardown();
    // Still enabled, so stay armed: the wake/watchdog effects — both gated on
    // `active` — are what request the token that revives us. Leaving `active`
    // false here would disarm self-healing for the rest of the page's life on
    // a transient BFF/core blip.
    active.current = true;
    setStatus('idle');
  }, [
    enabled,
    token,
    // Bumped by requestRebuild after a teardown: re-runs this effect so the
    // replacement Device is built by the exact same code as the first one.
    rebuildNonce,
    applyGainTo,
    teardown,
    clearVmGuard,
    muteRemoteAudio,
    unmuteRemoteAudio,
    acquireMic,
    refreshDevices,
    stampMicAcquired,
    stampMicFailed,
  ]);

  // Un-mute whether the card arrived during the blip window or, racing accept, just
  // before it. A voicemail hung up before the core's card watchdog never flips this,
  // so its audio stays silent for the life of the call. No-ops if we weren't muting.
  // The lead frame reached this hook — which is NOT the same as the card being on
  // screen, so nothing is reported here. `cockpit.card.rendered` is emitted from
  // CallPanel's mount effect instead: that component also requires `phase` to be
  // on_call/ending, and the two can drift. What this reports is the un-mute, via
  // `cockpit.blip.unmuted` with reason `card`.
  useEffect(() => {
    if (BLIP_MUTE_ENABLED && cardShown) unmuteRemoteAudio('card');
  }, [cardShown, unmuteRemoteAudio]);

  // What the browser says about the mic permission. NOT a gate — it words the banner,
  // and it is the fastest signal that an SDR flipped the site setting to Block
  // mid-shift. (The revocation algorithm ends the live track too, but nothing here
  // listens for that, so this is what we act on.)
  //
  // Supported for this descriptor in Chrome 64+, Firefox 132+ and Safari 16+, and it
  // can fail three ways: no `navigator.permissions` at all, a synchronous throw, or a
  // rejected promise for an unsupported name. All three land on 'unknown', which is
  // permissive — an unsupported browser must never read as a denied mic.
  useEffect(() => {
    // Mirrored into state as well as the health record: the copy has to re-render when
    // the permission resolves, and by then the verdict itself may already be settled.
    const apply = (permission: MicPermission): void => {
      micHealth.current.permission = permission;
      if (active.current) setMicPermission(permission);
      // A flip to Block mid-shift ends the live track, and nothing else here would
      // notice until the next acquire — which may be hours away, or never. So the
      // browser saying "denied" is itself the failure. It opens an episode like any
      // other, leaving the warn window to put it back before the picker goes.
      if (permission === 'denied' && micHealth.current.acquiredAt !== null) {
        stampMicFailed(new Error('Microphone permission denied'));
        return;
      }
      refreshMicVerdict();
    };
    let query: Promise<PermissionStatus> | undefined;
    try {
      query = navigator.permissions?.query({ name: 'microphone' as PermissionName });
    } catch {
      return;
    }
    if (!query) return;
    // A PermissionStatus that arrives after cleanup would attach a handler nothing can
    // detach — and StrictMode's mount/unmount/mount would leave two of them writing to
    // a hook instance that is gone.
    let cancelled = false;
    void query
      .then((status) => {
        if (cancelled) return;
        micPermissionStatus.current = status;
        apply(status.state);
        status.onchange = () => apply(status.state);
      })
      .catch(() => {
        if (!cancelled) apply('unknown');
      });
    return () => {
      cancelled = true;
      const status = micPermissionStatus.current;
      if (status) status.onchange = null;
      micPermissionStatus.current = null;
    };
  }, [refreshMicVerdict, stampMicFailed]);

  const micBanner = useMemo(
    () => micBannerCopy(micHealth.current, micVerdict),
    // micHealth is a ref; these are the two inputs that change what it says.
    [micVerdict, micPermission],
  );

  // Derived from the same two pieces of state the mic picker renders from, so the
  // highlight and the picker can never disagree about what is attached or live.
  const idleExternalMic = useMemo(
    () => buildAudioSnapshot(inputDevices, currentInputId).idleExternalLabel,
    [inputDevices, currentInputId],
  );

  // startCall: inbound calls auto-accept via `incoming`, so this is a no-op —
  // the real call's `accept` event drives the on_call status. Kept because the
  // cockpit phase effect calls it; left intentionally side-effect-free.
  const startCall = useCallback(() => {}, []);

  // endCall: hang up the real call if there is one. The call's `disconnect`
  // event resets status to ready. This is the one telephony side effect we make.
  const endCall = useCallback(() => {
    const call = callRef.current;
    if (call) {
      try {
        call.disconnect();
      } catch {
        // ignore
      }
      callRef.current = null;
    }
  }, []);

  return {
    status,
    awaitingRemote,
    humanUnconfirmed,
    silencePrompt,
    staySilenceGate,
    lastError,
    startCall,
    endCall,
    micSilence,
    outputVolume,
    setOutputVolume,
    getInputLevel,
    getOutputLevel,
    volumeSupported,
    quality,
    warnings,
    deviceRtt,
    playTestTone,
    runConnectionTest,
    connectionTest,
    inputDevices,
    outputDevices,
    currentInputId,
    currentOutputId,
    outputSelectionSupported,
    selectInputDevice,
    selectOutputDevice,
    deviceAlert,
    rebuildCapped,
    idleExternalMic,
    micState: micVerdict,
    micBanner,
    micPermission,
    micError: micHealth.current.lastError,
    micRetried,
    recheckMic,
    releaseMic,
    restoreMic,
    requestedEdge: edgePref.edge,
    edgeSource: edgePref.source,
    geoEdge: GEO_EDGE,
    edgePending: armedEdge !== undefined && edgePref.edge !== armedEdge,
    activeEdge,
    selectEdge,
    pqChromeMajor: PQ_CHROME_MAJOR,
    pqUi: pqUiState,
    markPqApplied,
    reportPqOpened,
  };
}
